
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { v4 as uuidv4 } from 'uuid';
import { useMemo } from 'react';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Drawer from '@material-ui/core/Drawer';
import Container from '@material-ui/core/Container';
import { IconButton, TextField, Avatar, Divider, CardHeader, Checkbox, FormControlLabel, Select, MenuItem, Switch, Tabs, Tab, Tooltip } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import SelectedPatientProfileDetails from './selectedPatientProfileDetails';
import SelectedPatientNotifications from './selectedPatientNotifications';
import { useSelector, useDispatch } from "react-redux";
import Skeleton from '@material-ui/lab/Skeleton';
import Box from '@material-ui/core/Box';
import ExerciseLibrary from '../exerciseLibraryFolder/exerciseLibrary';
import AdviceLibrary from '../exerciseLibraryFolder/adviceLibrary';
import ExerciseEditDialog from '../exerciseLibraryFolder/ExerciseEditDialog';
import ExerciseImage from '../exerciseLibraryFolder/ExerciseImage';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SelectedPatientDiagnosis from './SelectedPatientDiagnosis';
import SelectedPatientJourney from './SelectedPatientJourney';
import SelectedPatientPaymentSubscription from './selectedPatientPaymentSubscription';
import SelectedPatientInsurance from './selectedPatientInsurance';
import SelectedPatientAdvices from './selectedPatientAdvices';
import EHRBookingCalendar from '../Calendar/components/calendarClinic';
import ReportDialog from './ReportDialog';
import { saveReport } from './epicrisisUtils';
import { useFeature } from '../../../featureGating/featureGating';
import {
  buildExerciseEditParams,
  confirmExerciseProgramUpdate,
  normalizeScheduleType,
  normalizeEditedExerciseParams,
  persistExerciseProgramChanges,
  reviewExerciseProgramUpdate,
} from './exerciseProgramEditingUtils';
import { apiBaseUrl } from '../../../utilities/configureApiFetch';

const ADMIN_NAV_SHORTCUT_KEY = 'vp_adminNavigationShortcut';
const ADMIN_NAV_SHORTCUT_EVENT = 'vp:adminNavigationShortcut';

const PHASE_PROGRESSION_METRIC_OPTIONS = [
  { key: 'kpi/pain_baseline/current', label: 'Pain (current)', defaultOperator: 'lte', metricSource: 'kpi_template', kpiType: 'pain_baseline', kpiSubtype: 'current' },
  { key: 'kpi/pain_baseline/best', label: 'Pain (at best)', defaultOperator: 'lte', metricSource: 'kpi_template', kpiType: 'pain_baseline', kpiSubtype: 'best' },
  { key: 'kpi/pain_baseline/worst', label: 'Pain (at worst)', defaultOperator: 'lte', metricSource: 'kpi_template', kpiType: 'pain_baseline', kpiSubtype: 'worst' },
  { key: 'kpi/pain_baseline/frequency', label: 'Frequency', defaultOperator: 'lte', metricSource: 'kpi_template', kpiType: 'pain_baseline', kpiSubtype: 'frequency' },
  { key: 'kpi/function_goal/main_goal', label: 'Function (PSFS)', defaultOperator: 'gte', metricSource: 'kpi_template', kpiType: 'function_goal', kpiSubtype: 'main_goal' },
  { key: 'average_exercise_pain', label: 'Average exercise pain', defaultOperator: 'lte' },
  { key: 'exercise_level_progressions', label: 'Exercise level progressions', defaultOperator: 'gte' },
  { key: 'minimum_compliance', label: 'Compliance (%)', defaultOperator: 'gte' },
  { key: 'sessions_completed', label: 'Sessions completed', defaultOperator: 'gte' },
  { key: 'minimum_duration_weeks', label: 'Duration in phase (weeks)', defaultOperator: 'gte' },
];

const PHASE_PROGRESSION_TIME_METRIC_KEYS = ['minimum_duration_weeks', 'sessions_completed'];
const PHASE_PROGRESSION_COMBO_JOIN_OPTIONS = [
  { value: 'and', label: 'AND' },
  { value: 'or', label: 'OR' },
];

const PHASE_PROGRESSION_PAIN_METRIC_KEYS = new Set([
  'pain_reduction_percent',
  'pain_frequency_reduction_percent',
  'custom_pain_baseline',
  'average_exercise_pain',
]);

const PHASE_PROGRESSION_FUNCTION_METRIC_KEYS = new Set([
  'psfs_score',
  'custom_functional_goal',
]);

const PHASE_PROGRESSION_LEGACY_TO_KPI_MAP = {
  pain_reduction_percent: { kpiType: 'pain_baseline', kpiSubtype: 'current' },
  custom_pain_baseline: { kpiType: 'pain_baseline', kpiSubtype: 'current' },
  average_exercise_pain: { kpiType: 'pain_baseline', kpiSubtype: 'current' },
  pain_frequency_reduction_percent: { kpiType: 'pain_baseline', kpiSubtype: 'frequency' },
  psfs_score: { kpiType: 'function_goal', kpiSubtype: 'main_goal' },
  custom_functional_goal: { kpiType: 'function_goal', kpiSubtype: 'main_goal' },
};

const findMappedPhaseProgressionMetricOption = (legacyMetricKey, metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const mapping = PHASE_PROGRESSION_LEGACY_TO_KPI_MAP[legacyMetricKey];
  if (!mapping) {
    return null;
  }

  return metricOptions.find((option) => (
    String(option.kpiType || '').toLowerCase() === mapping.kpiType
    && String(option.kpiSubtype || '').toLowerCase() === mapping.kpiSubtype
  )) || null;
};

const normalizePhaseProgressionConditionMetricKey = (condition = {}, metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const metricKey = String(condition.metric_key || '').trim();
  if (!metricKey) {
    return '';
  }

  if (PHASE_PROGRESSION_TIME_METRIC_KEYS.includes(metricKey)) {
    return metricKey;
  }

  if (metricOptions.some((option) => option.key === metricKey)) {
    return metricKey;
  }

  const mappedMetric = findMappedPhaseProgressionMetricOption(metricKey, metricOptions);
  return mappedMetric ? mappedMetric.key : '';
};

const normalizePhaseProgressionMetricOptions = (items) => (
  Array.isArray(items) && items.length > 0
    ? items
      .map((item) => ({
        key: item.key,
        label: item.label,
        defaultOperator: item.defaultOperator || item.default_operator || 'gte',
        metricSource: item.metricSource || item.metric_source || '',
        kpiType: item.kpiType || item.kpi_type || '',
        kpiSubtype: item.kpiSubtype || item.kpi_subtype || '',
        questionIdentifier: item.questionIdentifier || item.question_identifier || '',
      }))
      .filter((item) => item.key && item.label)
      .filter((item) => PHASE_PROGRESSION_TIME_METRIC_KEYS.includes(item.key) || String(item.metricSource || '').toLowerCase().includes('kpi') || item.key.startsWith('kpi/'))
    : PHASE_PROGRESSION_METRIC_OPTIONS.map((item) => ({ ...item }))
);

const getPhaseProgressionTimeOptions = (metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => (
  metricOptions.filter((option) => PHASE_PROGRESSION_TIME_METRIC_KEYS.includes(option.key))
);

const getPhaseProgressionTransitionSource = (transition) => {
  if (!transition) {
    return {
      key: 'none',
      label: 'No rule selected',
      summary: '',
      notes: [],
      unrepresentedChecks: [],
      isExactMatch: true,
    };
  }

  if (transition.override_rule) {
    return {
      key: 'patient_override',
      label: 'Patient override',
      summary: 'This patient already has a stored override for the selected transition.',
      notes: [],
      unrepresentedChecks: [],
      isExactMatch: true,
    };
  }

  if (transition.effective_rule && transition.effective_rule_source === 'clinician_template') {
    return {
      key: 'clinician_template',
      label: 'Clinician template',
      summary: 'This treatment is currently following a stored clinician template for the selected transition.',
      notes: [],
      unrepresentedChecks: [],
      isExactMatch: true,
    };
  }

  if (transition.fallback_rule_preset || transition.effective_rule_source === 'legacy_fallback') {
    const fallbackPreset = transition.fallback_rule_preset || {};
    return {
      key: 'legacy_fallback',
      label: 'Preset standard criteria',
      summary: 'Preset standard criteria loaded. Review and adjust before saving.',
      notes: Array.isArray(fallbackPreset.notes) ? fallbackPreset.notes : [],
      unrepresentedChecks: Array.isArray(fallbackPreset.unrepresented_checks) ? fallbackPreset.unrepresented_checks : [],
      isExactMatch: Boolean(fallbackPreset.is_exact_match),
    };
  }

  return {
    key: 'none',
    label: 'No stored rule',
    summary: '',
    notes: [],
    unrepresentedChecks: [],
    isExactMatch: true,
  };
};

const getAvailablePhaseProgressionMetricOptions = (conditions = [], conditionIndex = 0, metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const currentCondition = conditions[conditionIndex] || {};
  const currentMetricKey = conditions[conditionIndex]?.metric_key;
  const usedMetricKeys = new Set(
    conditions
      .filter((_condition, index) => index !== conditionIndex)
      .map((condition) => condition.metric_key)
      .filter(Boolean)
  );
  const optionPool = conditionIndex === 0 ? getPhaseProgressionTimeOptions(metricOptions) : metricOptions;
  const filteredOptions = optionPool.filter((option) => option.key === currentMetricKey || !usedMetricKeys.has(option.key));

  if (currentMetricKey && !filteredOptions.some((option) => option.key === currentMetricKey)) {
    return [
      {
        key: currentMetricKey,
        label: currentCondition.label || currentMetricKey,
        defaultOperator: currentCondition.operator || 'gte',
        metricSource: currentCondition.metric_source || '',
        kpiType: currentCondition.kpi_type || '',
        kpiSubtype: currentCondition.kpi_subtype || '',
        questionIdentifier: currentCondition.question_identifier || '',
      },
      ...filteredOptions,
    ];
  }

  return filteredOptions;
};

const getNextPhaseProgressionMetricKey = (conditions = [], conditionIndex = 0, metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const availableOptions = getAvailablePhaseProgressionMetricOptions(conditions, conditionIndex, metricOptions);
  return availableOptions.length > 0
    ? availableOptions[0].key
    : (conditionIndex === 0 ? 'minimum_duration_weeks' : 'minimum_compliance');
};

const getPhaseProgressionMetricType = (condition = {}) => {
  const metricKey = String(condition.metric_key || '').trim();
  const explicitKpiType = String(condition.kpi_type || '').trim().toLowerCase();

  if (PHASE_PROGRESSION_PAIN_METRIC_KEYS.has(metricKey) || explicitKpiType === 'pain_baseline' || metricKey.startsWith('kpi/pain_baseline/')) {
    return 'pain';
  }

  if (PHASE_PROGRESSION_FUNCTION_METRIC_KEYS.has(metricKey) || explicitKpiType === 'function_goal' || metricKey.startsWith('kpi/function_goal/')) {
    return 'function';
  }

  return 'default';
};

const getPhaseProgressionThresholdLabel = (condition = {}) => {
  const metricType = getPhaseProgressionMetricType(condition);
  if (metricType === 'pain') return 'Min. reduction (%)';
  if (metricType === 'function') return 'Min. score (0-100)';
  return 'Minimum';
};

const createPhaseProgressionComboId = () => `phase-combo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const buildDefaultPhaseProgressionConditionDrafts = (metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const timeOptions = getPhaseProgressionTimeOptions(metricOptions);
  const anchorMetricKey = timeOptions[0]?.key || 'minimum_duration_weeks';
  const additionalMetricKey = metricOptions.find((option) => option.key !== anchorMetricKey)?.key || 'minimum_compliance';
  return [
    buildPhaseProgressionConditionDraft(anchorMetricKey, {}, metricOptions),
    buildPhaseProgressionConditionDraft(additionalMetricKey, {}, metricOptions),
  ];
};

const orderPhaseProgressionConditionDrafts = (conditions = [], metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const drafts = Array.isArray(conditions)
    ? conditions
      .map((condition) => {
        const normalizedMetricKey = normalizePhaseProgressionConditionMetricKey(condition, metricOptions);
        if (!normalizedMetricKey) {
          return null;
        }
        return buildPhaseProgressionConditionDraft(normalizedMetricKey, condition, metricOptions);
      })
      .filter(Boolean)
    : [];

  if (drafts.length === 0) {
    return buildDefaultPhaseProgressionConditionDrafts(metricOptions);
  }

  const anchorIndex = drafts.findIndex((condition) => PHASE_PROGRESSION_TIME_METRIC_KEYS.includes(condition.metric_key));
  const anchorCondition = anchorIndex >= 0
    ? drafts[anchorIndex]
    : buildPhaseProgressionConditionDraft('minimum_duration_weeks', {}, metricOptions);

  const remainingConditions = drafts.filter((_condition, index) => index !== anchorIndex);
  return [anchorCondition, ...remainingConditions].slice(0, 3);
};

const buildDefaultPhaseProgressionConditionCombo = (metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS, comboJoinOperator = 'and') => ({
  localId: createPhaseProgressionComboId(),
  combo_join_operator: comboJoinOperator,
  conditions: buildDefaultPhaseProgressionConditionDrafts(metricOptions),
});

const orderPhaseProgressionConditionCombos = (conditions = [], metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return [buildDefaultPhaseProgressionConditionCombo(metricOptions)];
  }

  const groupedConditions = conditions.reduce((groups, condition) => {
    const rawComboIndex = Number.isFinite(Number(condition?.combo_index)) ? Number(condition.combo_index) : 0;
    const comboIndex = rawComboIndex >= 0 ? rawComboIndex : 0;
    if (!groups.has(comboIndex)) {
      groups.set(comboIndex, {
        localId: createPhaseProgressionComboId(),
        combo_join_operator: comboIndex === 0 ? 'and' : (String(condition?.combo_join_operator || 'or').toLowerCase() === 'and' ? 'and' : 'or'),
        sourceConditions: [],
      });
    }
    groups.get(comboIndex).sourceConditions.push(condition || {});
    return groups;
  }, new Map());

  const orderedCombos = Array.from(groupedConditions.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([comboIndex, combo], normalizedIndex) => ({
      localId: combo.localId,
      combo_join_operator: normalizedIndex === 0 ? 'and' : combo.combo_join_operator,
      conditions: orderPhaseProgressionConditionDrafts(combo.sourceConditions, metricOptions),
    }));

  return orderedCombos.length > 0 ? orderedCombos : [buildDefaultPhaseProgressionConditionCombo(metricOptions)];
};

const EXERCISE_ADJUSTMENT_MODE_OPTIONS = [
  {
    value: 'auto_replace',
    label: 'Adaptive',
    description: 'Automatically replace exercises when adaptive rules trigger.',
  },
  {
    value: 'disabled',
    label: 'Manual only',
    description: 'Keep exercise adjustments fully manual for this treatment.',
  },
];

const PHASE_PROGRESSION_SELECT_MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    style: {
      borderRadius: 14,
      marginTop: 8,
    },
  },
};

const buildPhaseProgressionConditionDraft = (metricKey = 'minimum_compliance', source = {}, metricOptions = PHASE_PROGRESSION_METRIC_OPTIONS) => {
  const metric = metricOptions.find((option) => option.key === metricKey)
    || PHASE_PROGRESSION_METRIC_OPTIONS.find((option) => option.key === metricKey)
    || (metricKey ? {
      key: metricKey,
      label: source.label || metricKey,
      defaultOperator: source.operator || 'gte',
      metricSource: source.metric_source || source.metricSource || '',
      kpiType: source.kpi_type || source.kpiType || '',
      kpiSubtype: source.kpi_subtype || source.kpiSubtype || '',
      questionIdentifier: source.question_identifier || source.questionIdentifier || '',
    } : null)
    || metricOptions[0]
    || PHASE_PROGRESSION_METRIC_OPTIONS[0];
  return {
    metric_key: metric.key,
    operator: source.operator || metric.defaultOperator,
    threshold_value: source.threshold_value ?? '',
    metric_source: source.metric_source || source.metricSource || metric.metricSource || '',
    kpi_type: source.kpi_type || source.kpiType || metric.kpiType || '',
    kpi_subtype: source.kpi_subtype || source.kpiSubtype || metric.kpiSubtype || '',
    question_identifier: source.question_identifier || source.questionIdentifier || metric.questionIdentifier || '',
    exercise_identifiers_text: Array.isArray(source.exercise_identifiers) ? source.exercise_identifiers.join(', ') : '',
  };
};

const useStyles = makeStyles((theme) => ({
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.45vw, 6px) clamp(10px, 1.1vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(84px, 8vw, 100px)',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.45vw, 6px) clamp(10px, 1.1vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(84px, 8vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  removeButton: {
    transition: 'background 0.2s',
    backgroundColor: '#F0F8FF',
    color: '#FF7F27',
      fontWeight: 'bold',
      maxHeight: 30,
      zIndex: 2,
    '&:hover': {
      backgroundColor: '#e6eaf0', // light grey that fits the theme
      color: '#FF7F27',
      fontWeight: 'bold',
      maxHeight: 30,
      zIndex: 2
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      color: '#002D55',
      '& fieldset': {
        borderColor: '#ccc',
      },
      '&:hover fieldset': {
        borderColor: '#FF7F27',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FF7F27',
      },
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent',
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)',
    },
  },
  phaseProgressionSelectField: {
    '& .MuiSelect-select': {
      color: '#002D55',
      fontWeight: 500,
      fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
      borderRadius: 14,
    },
  },
  phaseProgressionCompactField: {
    '& .MuiOutlinedInput-root': {
      height: 30,
      borderRadius: 14,
      backgroundColor: '#FFFFFF',
      overflow: 'hidden',
      color: '#002D55',
      '& fieldset': {
        borderColor: '#ccc',
      },
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: 6.5,
      paddingBottom: 6.5,
    },
    '& .MuiSelect-select.MuiSelect-select': {
      minHeight: 14,
      paddingTop: 6,
      paddingBottom: 6,
      display: 'flex',
      alignItems: 'center',
    },
  },
  alwaysOrangeUnderline: {
    '& .MuiInput-underline:before, & .MuiInput-underline:after': {
      borderBottom: '2px solid #FF7F27 !important',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27 !important',
    },
    '& .MuiInputBase-root': {
      color: '#002D55',
    },
  },
  alwaysShowSpinButtons: {
    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
      opacity: 1,
      pointerEvents: 'all',
      WebkitAppearance: 'auto',
    },
    // For Firefox
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
  },
  cardGrid: {
    marginTop: "5%",
    marginBottom: 'clamp(48px, 7vw, 100px)',
  },
  cardFiltered: {
    height: '97%',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
  },
  cardOriginal: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12
  },
  cardMedia: {
    paddingTop: '56.25%', // 16:9
  },
  exerciseTypographyCardStyling: {
    flexGrow: 1,
    color: "#002D55",
    textAlign: "left",
    marginBottom: 10,
    fontSize: 'clamp(0.81rem, 1.27vw, 1.15rem)',
    borderRadius: 16
  },
  footer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(6),
  },
  tabsRoot: {
    backgroundColor: '#f7faff',
    borderRadius: 12,
    marginBottom: "0.6%",
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,45,85,0.12)',
    [theme.breakpoints.only('md')]: {
      height: 'clamp(24px, calc(4px + 4.5vw), 38px)',
    },
  },
  tabsControlRoot: {
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    '& .MuiTabs-flexContainer': {
      height: '100%',
      alignItems: 'center',
    },
    [theme.breakpoints.only('md')]: {
      minHeight: 'clamp(24px, calc(4px + 4.5vw), 38px)',
      height: 'clamp(24px, calc(4px + 4.5vw), 38px)',
    },
  },
  tabRoot: {
    fontWeight: 600,
    fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
    textTransform: 'uppercase',
    color: '#002D55',
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    minWidth: 'clamp(60px, 6.9vw, 100px)',
    padding: '0 clamp(4px, 0.7vw, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    '&.Mui-selected': {
      color: '#002D55',
    },
    '&:hover': {
      color: '#FF7F27',
    },
  },
  tabIndicator: {
    backgroundColor: '#FF7F27',
    height: 3,
    borderRadius: 18,
    bottom: 0,
  },
  exCardParams: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.65rem, 0.9vw, 0.83rem)',
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.7rem, 0.95vw, 0.92rem)',
    },
  },
  orangeSelect: {
    background: '#FFFFFF',
    color: '#002D55',
    fontWeight: 500,
    fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
    border: '1px solid #ccc',
    borderRadius: 14,
    padding: 'clamp(2.3px, 0.29vw, 3.5px) clamp(12px, 0.15vw, 18px)',
    paddingLeft: '0px !important', 
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    '& .MuiSelect-select': {
      padding: 'clamp(0px, 0.29vw, 0px) clamp(12px, 0.15vw, 24px) !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      background: 'transparent',
    },
    '&:focus': {
      borderColor: '#FF7F27',
      outline: '#FF7F27 solid 1px',
    },
    '&:hover': {
      borderColor: '#FF7F27',
    },
  },
  menuItem: {
    fontSize: 'clamp(0.69rem, 0.69vw, 0.93rem) !important',
    color: '#002D55',
  },
  cardClickable: {
    cursor: 'pointer',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    border: '1px solid transparent',
    '&:hover': {
      borderColor: '#FF7F27',
    },
  },
  iconButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    border: '1px solid transparent',
    transition: 'border-color 0.2s',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      borderColor: '#FF7F27',
    },
  },
  panelHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 6,
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    paddingLeft: 'clamp(10px, calc(2px + 0.625vw), 16px)',
    paddingRight: 'clamp(10px, calc(2px + 0.625vw), 16px)',
    paddingTop: 'clamp(8px, calc(1px + 0.55vw), 12px)',
    paddingBottom: 'clamp(8px, calc(1px + 0.55vw), 12px)',
    boxSizing: 'border-box',
    borderBottom: '2px solid #FF7F27',
  },
  panelIconButton: {
    width: 'clamp(22px, calc(8px + 1.25vw), 32px)',
    height: 'clamp(22px, calc(8px + 1.25vw), 32px)',
    padding: 'clamp(2px, calc(-0.5px + 0.3906vw), 6px)',
    borderRadius: 8,
    '& .MuiSvgIcon-root': {
      fontSize: 'clamp(13px, calc(4px + 0.9vw), 20px)',
    },
  },
  editAdviceDialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
  },
  editAdviceDialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  editAdviceDialogTitleText: {
    flex: 1,
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
  },
  editAdviceDialogCloseButton: {
    color: '#64748B',
    padding: 6,
  },
  editAdviceDialogContent: {
    padding: '24px !important',
    overflow: 'auto',
    backgroundColor: '#e6eaf0',
  },
  editAdviceDialogSectionLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  editAdviceDialogSectionPanel: {
    backgroundColor: '#f7faff',
    borderRadius: 18,
    padding: 16,
    marginBottom: 24,
    border: '1px solid #dce8f5',
  },
  editAdviceDialogField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#ffffff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 14,
      borderColor: '#dddddd',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.65rem, calc(1px + 0.72vw), 0.9rem)',
      color: '#666666',
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
      color: '#002D55',
    },
  },
  editAdviceDialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
  },
}));


function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const WEIGHT_DOSE_OPTIONS = {
  weight_kg: { field: 'weightKg', label: 'Weight (Kg)', tooltip: 'Kilograms per rep/set.' },
  weight_total_kg: { field: 'weightTotalKg', label: 'Weight (Total Kg)', tooltip: 'Total kilograms lifted per set.' },
  weight_lbs: { field: 'weightLbs', label: 'Weight (Lbs)', tooltip: 'Pounds per rep/set.' },
  weight_total_lbs: { field: 'weightTotalLbs', label: 'Weight (Total Lbs)', tooltip: 'Total pounds lifted per set.' },
};

function SelectedPatientProgram(props) {
  const { profiles, diagnosis, phases, advices, allDiagnoses, sessionData, assessmentDate, selectedPatientDataLoading, selectedPatient, exercises,
    fetchExercisesForPatient, fetchExercisesForPhase,
    fetchPatientAllData,
    refreshPatientTableData,
    onProfileSaved,
    selectedPhaseId, setSelectedPhaseId, currentPhaseId,
    libraryExercises, libraryExercisesLoaded
  } = props;
  
  // NOTE: Avoid render-time logging here to prevent UI stalls on large payloads.

  // Phase options (stable even if phases is undefined)
  const exercisePhases = React.useMemo(() => (
    phases
      ? phases.map((phase, idx) => ({
          value: phase.id,
          label: `${phase.name || `Phase ${idx + 1}`}`,
          phaseId: phase.id,
        }))
      : []
  ), [phases]);
  
  // Restore activeTab from localStorage, default to the embedded calendar tab.
  const [activeTab, setActiveTabState] = useState(() => {
    try {
      return localStorage.getItem('vp_activeTab') || 'clinicCalendar';
    } catch (e) {
      return 'clinicCalendar';
    }
  });
  const canAdaptiveExercise = useFeature('adaptive_exercise');

  const [patientDetailsTab, setPatientDetailsTab] = useState(() => {
    try {
      return localStorage.getItem('vp_patientDetailsTab') || 'payment';
    } catch (e) {
      return 'payment';
    }
  });
  const history = useHistory();
  const [selectedInsuranceTreatmentId, setSelectedInsuranceTreatmentId] = useState(null);
  
  // Derive treatments array from selectedPatient.diagnosis (similar to patientList.js)
  const deriveTreatmentsFromProfile = React.useCallback((patient) => {
    if (!patient || !Array.isArray(patient.diagnosis)) return [];
    const patientId = patient.user_id || patient.id || 'patient';
    return patient.diagnosis.map((diag, idx) => ({
      id: diag.treatment_id || `${patientId}-treat-${idx}`,
      treatment_id: diag.treatment_id || null,
      diagnosis_id: diag.diagnosis_id || diag.id || null,
      diagnosis_identifier: diag.diagnosis_identifier || diag.identifier || null,
      name: diag.treatment_name || null,
      primary_complaint_key: diag.primary_complaint_key || '',
      primary_complaint_label: diag.primary_complaint_label || '',
      exercise_library_region: diag.exercise_library_region || '',
      diagnosis: { diagnosis: diag.title || diag.identifier || diag.diagnosis || 'Unknown Diagnosis' }
    }));
  }, []);

  // Auto-select treatment when patient changes: if only 1 treatment, use that; otherwise use latest
  React.useEffect(() => {
    if (selectedPatient) {
      const treatments = deriveTreatmentsFromProfile(selectedPatient);
      if (treatments.length === 1) {
        // Only one treatment available - select it
        setSelectedInsuranceTreatmentId(treatments[0].id);
      } else if (treatments.length > 1 && !selectedInsuranceTreatmentId) {
        // Multiple treatments - select the most recent (last in array)
        setSelectedInsuranceTreatmentId(treatments[treatments.length - 1].id);
      }
    }
  }, [selectedPatient, selectedInsuranceTreatmentId, deriveTreatmentsFromProfile]);

  const setPatientDetailsTabState = (tabKey) => {
    setPatientDetailsTab(tabKey);
    try {
      localStorage.setItem('vp_patientDetailsTab', tabKey);
    } catch (e) {
      console.error('Failed to persist patientDetailsTab:', e);
    }
  };

  const openPatientDetailsTab = (tabKey) => {
    setPatientDetailsTabState(tabKey);
    setActiveTab('patientDetails');
  };

  useEffect(() => {
    if (!canAdaptiveExercise && activeTab === 'exerciseProgram') {
      setActiveTabState('Overview');
    }
  }, [activeTab, canAdaptiveExercise]);

  const openClinicStatistics = () => {
    try {
      localStorage.setItem('vp_clinicDetailsTabIndex', '3');
    } catch (e) {
      console.error('Failed to persist clinicDetails tab index:', e);
    }

    history.push('/my_clinic_account');
  };

  const clearAdminNavigationShortcut = useCallback(() => {
    try {
      localStorage.removeItem(ADMIN_NAV_SHORTCUT_KEY);
    } catch (e) {
      // ignore
    }
  }, []);

  const handleAdminNavigationShortcut = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return;

    const shortcutTarget = String(payload.target || '').trim();
    const patientDetailsTarget = String(payload.patientDetailsTab || payload.tab || '').trim();

    const runShortcut = (label, action) => runWithJournalUnsavedGuard(label, action);

    switch (shortcutTarget) {
      case 'clinicCalendar':
      case 'calendar':
        runShortcut('opening the calendar tab', () => setActiveTab('clinicCalendar'));
        break;
      case 'Overview':
      case 'overview':
        runShortcut('switching to the overview tab', () => setActiveTab('Overview'));
        break;
      case 'journal':
        runShortcut('opening the journal tab', () => setActiveTab('journal'));
        break;
      case 'exerciseProgram':
      case 'program':
        runShortcut('opening the program tab', () => handleProgramTabClick());
        break;
      case 'patientPayment':
      case 'payment':
        runShortcut('opening payment details', () => openPatientDetailsTab('payment'));
        break;
      case 'patientInsurance':
      case 'insurance':
        runShortcut('opening insurance details', () => openPatientDetailsTab('insurance'));
        break;
      case 'patientDetails':
      case 'patient':
        runShortcut('opening patient details', () => openPatientDetailsTab(patientDetailsTarget || 'personalInformation'));
        break;
      case 'patientPersonalInformation':
      case 'personalInformation':
        runShortcut('opening patient details', () => openPatientDetailsTab('personalInformation'));
        break;
      default:
        return;
    }

    clearAdminNavigationShortcut();
  }, [clearAdminNavigationShortcut, handleProgramTabClick, openPatientDetailsTab, runWithJournalUnsavedGuard, setActiveTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleStorage = (event) => {
      if (event?.key !== ADMIN_NAV_SHORTCUT_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        handleAdminNavigationShortcut(payload);
      } catch (e) {
        // ignore malformed payloads
      }
    };

    const handleCustomEvent = (event) => {
      handleAdminNavigationShortcut(event?.detail || null);
    };

    const handleFocus = () => {
      try {
        const raw = localStorage.getItem(ADMIN_NAV_SHORTCUT_KEY);
        if (!raw) return;
        const payload = JSON.parse(raw);
        handleAdminNavigationShortcut(payload);
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(ADMIN_NAV_SHORTCUT_EVENT, handleCustomEvent);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(ADMIN_NAV_SHORTCUT_EVENT, handleCustomEvent);
      window.removeEventListener('focus', handleFocus);
    };
  }, [handleAdminNavigationShortcut]);

  function runWithJournalUnsavedGuard(label, action) {
    if (typeof action !== 'function') return;
    if (typeof window !== 'undefined') {
      const guard = window.__vpJournalUnsavedGuard;
      const isEditingMode = guard && typeof guard.isEditingMode === 'function'
        ? guard.isEditingMode()
        : false;
      if (isEditingMode && guard && typeof guard.request === 'function') {
        guard.request(label, action);
        return;
      }
    }
    action();
  }
  
  // Persist activeTab to localStorage whenever it changes
  function setActiveTab(tab) {
    setActiveTabState(tab);
    try {
      localStorage.setItem('vp_activeTab', tab);
    } catch (e) {
      console.error('Failed to persist activeTab:', e);
    }
  }

  // Track last treatment to detect changes
  const lastTreatmentIdRef = useRef(null);
  // Track last advices treatment to prevent duplicate fetches
  const lastAdvicesTreatmentRef = useRef(null);
  
  // Phase selection is now fully managed by parent (patientList.js)
  // Remove auto-select logic from child to prevent overriding user selections

  

  // leftTab state removed; dropdown is fully controlled by selectedPhaseId from parent
  const classes = useStyles();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  // Get token directly from Redux on each render (don't store in state to avoid stale token)
  const token = useSelector(state => state.auth.token);
  const [isLoggedIn, setIsLoggedIn] = useState(useSelector(state => state.auth.isLoggedIn));
  const [isLoading, setIsLoading] = useState(false);
  const [latestDiagnosis, setLatestDiagnosis] = useState(null);
  
  // Compute active diagnosis based on selected treatment/diagnosis selection
  // Prefer the consolidated diagnosis prop from parent (scoped by treatment_id)
  const activeDiagnosis = React.useMemo(() => {
    try {
      // If parent passed a scoped diagnosis object (not array), use it directly
      if (diagnosis && typeof diagnosis === 'object' && !Array.isArray(diagnosis)) {
        return diagnosis;
      }
      // Otherwise, compute from selectedPatient data
      const diags = (selectedPatient && Array.isArray(selectedPatient.diagnosis)) ? selectedPatient.diagnosis : [];
      const chosenTid = selectedPatient && (selectedPatient.treatment_id || (selectedPatient.treatment && selectedPatient.treatment.id));
      const chosenDiagId = selectedPatient && (selectedPatient.selected_diagnosis_id || selectedPatient.diagnosis_id);
      const chosenDiagIdent = selectedPatient && (selectedPatient.selected_diagnosis_identifier || selectedPatient.diagnosisIdentifier);
      // Primary: match by treatment_id (most precise)
      if (chosenTid && diags.length) {
        const m = diags.find(d => (d && (d.treatment_id === chosenTid || (d.treatment && d.treatment.id === chosenTid))));
        if (m) return m;
      }
      // Secondary: match by diagnosis UUID
      if (chosenDiagId && diags.length) {
        // When multiple treatments share the same diagnosis, prefer the one whose treatment_id matches
        const candidates = diags.filter(d => (d && ((d.diagnosis_id || d.id) === chosenDiagId)));
        if (candidates.length === 1) return candidates[0];
        if (candidates.length > 1 && chosenTid) {
          const exact = candidates.find(d => d.treatment_id === chosenTid);
          if (exact) return exact;
        }
        if (candidates.length > 0) return candidates[candidates.length - 1];
      }
      // Tertiary: match by diagnosis identifier string
      if (chosenDiagIdent && diags.length) {
        const m = diags.find(d => (d && (d.identifier === chosenDiagIdent || d.diagnosis_identifier === chosenDiagIdent)));
        if (m) return m;
      }
      if (diags.length === 1) return diags[0];
      // fallback: latestDiagnosis from fetch, else last in array
      if (latestDiagnosis) return latestDiagnosis;
      if (diags.length > 0) return diags[diags.length - 1];
      return null;
    } catch (e) { return latestDiagnosis || null; }
  }, [selectedPatient, diagnosis, latestDiagnosis]);

  const selectedTreatmentComplaintRegion = React.useMemo(() => {
    const selectedTreatmentId = selectedPatient?.treatment_id || props.treatmentId || '';
    return (selectedPatient?.diagnosis || []).find((item) => (
      String(item?.treatment_id || item?.id || '') === String(selectedTreatmentId)
    ))?.exercise_library_region || '';
  }, [props.treatmentId, selectedPatient?.diagnosis, selectedPatient?.treatment_id]);

  const treatmentExerciseLibraryFilters = React.useMemo(() => ({
    filterBodyRegion: activeDiagnosis?.exercise_library_region
      || selectedPatient?.exercise_library_region
      || selectedTreatmentComplaintRegion,
  }), [activeDiagnosis?.exercise_library_region, selectedPatient?.exercise_library_region, selectedTreatmentComplaintRegion]);
  const treatmentExerciseLibraryFiltersKey = `${selectedPatient?.treatment_id || props.treatmentId || ''}-${treatmentExerciseLibraryFilters.filterBodyRegion}`;

  const diagnosisIdentifierForReport = React.useMemo(() => {
    const fromActive = activeDiagnosis && (activeDiagnosis.diagnosis_identifier || activeDiagnosis.identifier);
    if (fromActive) return fromActive;
    const fromSelected = selectedPatient && (selectedPatient.selected_diagnosis_identifier || selectedPatient.diagnosisIdentifier);
    if (fromSelected) return fromSelected;
    const diags = selectedPatient && Array.isArray(selectedPatient.diagnosis) ? selectedPatient.diagnosis : [];
    const latest = diags.length ? diags[diags.length - 1] : null;
    return latest ? (latest.diagnosis_identifier || latest.identifier || null) : null;
  }, [activeDiagnosis, selectedPatient]);

  const selectedTreatmentId = props.selectedTreatmentId || selectedPatient?.treatment_id || props.treatmentId;

  React.useEffect(() => {
    const uid = selectedPatient && (selectedPatient.user_id || selectedPatient.id || (selectedPatient.user && selectedPatient.user.id));
    // console.log('[SelectedPatientProgram] diagnosis props changed', {
    //   uid,
    //   selectedPatientTreatmentId: selectedPatient && selectedPatient.treatment_id,
    //   selectedDiagId: selectedPatient && selectedPatient.selected_diagnosis_id,
    //   diagnosisProp: diagnosis,
    //   activeDiagnosis
    // });
  }, [selectedPatient, diagnosis, activeDiagnosis]);
  
  // Restore showAdviceInExerciseTab from localStorage, default to false
  const [showAdviceInExerciseTab, setShowAdviceInExerciseTabState] = useState(() => {
    try {
      return localStorage.getItem('vp_showAdviceInExerciseTab') === 'true';
    } catch (e) {
      return false;
    }
  });
  
  // Persist showAdviceInExerciseTab to localStorage whenever it changes
  const setShowAdviceInExerciseTab = (value) => {
    setShowAdviceInExerciseTabState(value);
    try {
      localStorage.setItem('vp_showAdviceInExerciseTab', String(value));
    } catch (e) {
      console.error('Failed to persist showAdviceInExerciseTab:', e);
    }
  };

  // Snackbar state for save feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [programJournalConfirmation, setProgramJournalConfirmation] = useState(null);
  // Active session warning state
  const [activeSessionInfo, setActiveSessionInfo] = useState(null);
  const [activeSessionLocked, setActiveSessionLocked] = useState(false);

  // Exercise adjustment mode state
  const [adjustmentMode, setAdjustmentMode] = useState('auto_replace');
  const [clinicAdjustmentModeDefault, setClinicAdjustmentModeDefault] = useState('auto_replace');
  /**
   * REGULATORY COMPLIANCE ANCHOR: CLASS I / NON-MDR SAFEGUARD
   *
   * This retained pending-suggestion shell is intentionally dormant.
   * The backend no longer emits live suggestive CDS workflows, and the UI
   * sanitizes legacy suggest_only modes to Manual Only before activation.
   */
  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [adjustmentModeLoading, setAdjustmentModeLoading] = useState(false);
  const [phaseProgressionData, setPhaseProgressionData] = useState(null);
  const [phaseProgressionLoading, setPhaseProgressionLoading] = useState(false);
  const [phaseProgressionDialogOpen, setPhaseProgressionDialogOpen] = useState(false);
  const [selectedPhaseTransitionKey, setSelectedPhaseTransitionKey] = useState('');
  const [phaseRuleDraftCombos, setPhaseRuleDraftCombos] = useState(() => [buildDefaultPhaseProgressionConditionCombo()]);
  const [rememberPhaseRuleForAllPatients, setRememberPhaseRuleForAllPatients] = useState(false);
  const [phaseProgressionSaving, setPhaseProgressionSaving] = useState(false);
  const [phasePromotionTarget, setPhasePromotionTarget] = useState('');
  const [phasePromotionRationale, setPhasePromotionRationale] = useState('');
  const [phasePromotionLoading, setPhasePromotionLoading] = useState(false);
  const [phaseProgressionSection, setPhaseProgressionSection] = useState('settings');

  const sanitizeAdjustmentMode = useCallback((mode, fallback = 'auto_replace') => {
    const resolvedMode = mode || fallback;
    return resolvedMode === 'suggest_only' ? 'disabled' : resolvedMode;
  }, []);

  const sanitizeAdjustmentModeOverride = useCallback((modeOverride) => {
    const resolvedOverride = modeOverride || '__inherit__';
    return resolvedOverride === 'suggest_only' ? 'disabled' : resolvedOverride;
  }, []);

  const getAdjustmentModeLabel = useCallback((mode) => {
    if (mode === 'suggest_only') return 'Manual Only';
    if (mode === 'disabled') return 'Manual Only';
    return 'Adaptive';
  }, []);

  const getPhaseTransitionKey = useCallback((transition) => {
    if (!transition) return '';
    return `${transition.source_phase_index}->${transition.target_phase_index}`;
  }, []);

  const phaseProgressionMetricOptions = useMemo(
    () => normalizePhaseProgressionMetricOptions(phaseProgressionData?.available_metrics),
    [phaseProgressionData?.available_metrics]
  );

  const seedPhaseProgressionDraft = useCallback((transition, metricOptionsOverride = null) => {
    const sourceConditions = transition?.override_rule?.conditions
      || transition?.effective_rule?.conditions
      || transition?.fallback_rule_preset?.conditions
      || [];
    const metricOptions = metricOptionsOverride || phaseProgressionMetricOptions;
    const isLegacyFallback = transition?.effective_rule_source === 'legacy_fallback'
      || Boolean(transition?.fallback_rule_preset);
    const normalizedConditions = sourceConditions
      .map((condition) => {
        const normalizedMetricKey = normalizePhaseProgressionConditionMetricKey(condition, metricOptions);
        if (!normalizedMetricKey) {
          return null;
        }

        const mappedOption = metricOptions.find((option) => option.key === normalizedMetricKey) || {};
        return {
          ...condition,
          metric_key: normalizedMetricKey,
          metric_source: condition.metric_source || mappedOption.metricSource || '',
          kpi_type: condition.kpi_type || mappedOption.kpiType || '',
          kpi_subtype: condition.kpi_subtype || mappedOption.kpiSubtype || '',
          question_identifier: condition.question_identifier || mappedOption.questionIdentifier || '',
        };
      })
      .filter(Boolean);

    const seededConditions = (isLegacyFallback && normalizedConditions.length === 0)
      ? [{
        metric_key: 'minimum_duration_weeks',
        operator: 'gte',
        threshold_value: 2,
      }]
      : normalizedConditions;

    setPhaseRuleDraftCombos(orderPhaseProgressionConditionCombos(seededConditions, metricOptions));
    setRememberPhaseRuleForAllPatients(false);
    setPhasePromotionTarget(transition ? String(transition.target_phase_index) : '');
    setPhasePromotionRationale('');
  }, [phaseProgressionMetricOptions]);

  const fetchPhaseProgressionRules = useCallback(async () => {
    if (!selectedTreatmentId || !token) return;
    setPhaseProgressionLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/treatments/${selectedTreatmentId}/phase-progression-rules/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) {
        throw new Error('Unable to load phase progression rules.');
      }
      const data = await res.json();
      setPhaseProgressionData(data);
      setSelectedPhaseTransitionKey((currentKey) => {
        const transitions = Array.isArray(data.transitions) ? data.transitions : [];
        if (currentKey && transitions.some((transition) => getPhaseTransitionKey(transition) === currentKey)) {
          return currentKey;
        }
        const activeTransition = transitions.find((transition) => transition.source_phase_index === data.active_phase_index) || transitions[0];
        return activeTransition ? getPhaseTransitionKey(activeTransition) : '';
      });
    } catch (error) {
      console.warn('Failed to fetch phase progression rules', error);
    }
    setPhaseProgressionLoading(false);
  }, [getPhaseTransitionKey, selectedTreatmentId, token]);

  // Fetch adjustment mode from treatment settings
  useEffect(() => {
    if (!selectedTreatmentId || !token) return;
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/treatment/treatments/${selectedTreatmentId}/settings/`, {
          headers: { 'Authorization': `Token ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAdjustmentMode(sanitizeAdjustmentMode(data.exercise_adjustment_mode));
          setClinicAdjustmentModeDefault(sanitizeAdjustmentMode(data.clinic_exercise_adjustment_mode_default));
        }
      } catch (err) { console.warn('Failed to fetch treatment settings', err); }
    };
    fetchSettings();
  }, [sanitizeAdjustmentMode, sanitizeAdjustmentModeOverride, selectedTreatmentId, token]);

  useEffect(() => {
    fetchPhaseProgressionRules();
  }, [fetchPhaseProgressionRules]);

  /**
   * REGULATORY COMPLIANCE ANCHOR: CLASS I / NON-MDR SAFEGUARD
   *
   * This retained pending-suggestion fetch/action path is intentionally
   * dormant. Backend suggestive endpoints are hard-disabled for Class I use,
   * so this block remains only as a compatibility shell around legacy data.
   */
  // Fetch pending suggestions
  const fetchPendingSuggestions = useCallback(async () => {
    const treatmentId = selectedPatient?.treatment_id || props.treatmentId;
    const userId = selectedPatient?.user_id;
    if (!treatmentId || !userId || !token) return;
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/exercise-adjustments/${userId}/${treatmentId}/?status=pending`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPendingSuggestions(data);
      }
    } catch (err) { console.warn('Failed to fetch pending suggestions', err); }
  }, [selectedPatient?.treatment_id, selectedPatient?.user_id, props.treatmentId, token]);

  const handleAdjustmentModeChange = useCallback(async (nextMode) => {
    if (!selectedTreatmentId || !token) return;
    setAdjustmentModeLoading(true);
    try {
      const shouldInheritClinicDefault = nextMode === clinicAdjustmentModeDefault;
      const payload = {
        exercise_adjustment_mode_override: shouldInheritClinicDefault ? null : nextMode,
      };
      const res = await fetch(`${apiBaseUrl}/treatment/treatments/${selectedTreatmentId}/settings/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        const nextEffectiveMode = sanitizeAdjustmentMode(data.exercise_adjustment_mode);
        const nextOverride = sanitizeAdjustmentModeOverride(data.exercise_adjustment_mode_override);
        const nextClinicDefault = sanitizeAdjustmentMode(data.clinic_exercise_adjustment_mode_default);

        setAdjustmentMode(nextEffectiveMode);
        setClinicAdjustmentModeDefault(nextClinicDefault);
        setSnackbarMsg(
          nextOverride === '__inherit__'
            ? `Exercise adjustment mode now inherits the clinic default: ${getAdjustmentModeLabel(nextEffectiveMode)}.`
            : `Exercise adjustment mode override set to ${getAdjustmentModeLabel(nextEffectiveMode)}.`
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) { console.warn('Failed to update adjustment mode', err); }
    setAdjustmentModeLoading(false);
  }, [clinicAdjustmentModeDefault, getAdjustmentModeLabel, sanitizeAdjustmentMode, sanitizeAdjustmentModeOverride, selectedTreatmentId, token]);

  const selectedPhaseTransition = useMemo(() => {
    const transitions = phaseProgressionData?.transitions || [];
    return transitions.find((transition) => getPhaseTransitionKey(transition) === selectedPhaseTransitionKey) || null;
  }, [getPhaseTransitionKey, phaseProgressionData, selectedPhaseTransitionKey]);

  const selectedPhaseTransitionSource = useMemo(
    () => getPhaseProgressionTransitionSource(selectedPhaseTransition),
    [selectedPhaseTransition]
  );

  const activePhaseTransition = useMemo(() => {
    const transitions = phaseProgressionData?.transitions || [];
    return transitions.find((transition) => transition.source_phase_index === phaseProgressionData?.active_phase_index) || transitions[0] || null;
  }, [phaseProgressionData]);

  const manualPromotionOptions = useMemo(() => {
    const currentActivePhaseIndex = phaseProgressionData?.active_phase_index;
    if (Array.isArray(phases) && phases.length > 0) {
      return [...phases]
        .sort((left, right) => (left.index ?? left.phaseIndex ?? 0) - (right.index ?? right.phaseIndex ?? 0))
        .reduce((options, phase) => {
          const phaseIndex = phase.index ?? phase.phaseIndex;
          if (phaseIndex === undefined || phaseIndex === null || phaseIndex === currentActivePhaseIndex) {
            return options;
          }
          options.push({
            value: String(phaseIndex),
            label: phase.name || `Phase ${phaseIndex}`,
          });
          return options;
        }, []);
    }

    const transitions = phaseProgressionData?.transitions || [];
    const phaseMap = new Map();
    transitions.forEach((transition) => {
      if (transition.source_phase_index !== undefined) {
        phaseMap.set(String(transition.source_phase_index), {
          value: String(transition.source_phase_index),
          label: transition.source_phase_name || `Phase ${transition.source_phase_index}`,
        });
      }
      if (transition.target_phase_index !== undefined) {
        phaseMap.set(String(transition.target_phase_index), {
          value: String(transition.target_phase_index),
          label: transition.target_phase_name || `Phase ${transition.target_phase_index}`,
        });
      }
    });

    return Array.from(phaseMap.values()).filter((option) => option.value !== String(currentActivePhaseIndex));
  }, [phaseProgressionData, phases]);

  const handleOpenPhaseProgressionDialog = useCallback(() => {
    const transitionToOpen = activePhaseTransition || selectedPhaseTransition || null;
    if (transitionToOpen) {
      setSelectedPhaseTransitionKey(getPhaseTransitionKey(transitionToOpen));
      seedPhaseProgressionDraft(transitionToOpen);
    }
    setPhaseProgressionSection('settings');
    setPhaseProgressionDialogOpen(true);
  }, [activePhaseTransition, getPhaseTransitionKey, seedPhaseProgressionDraft, selectedPhaseTransition]);

  const handleClosePhaseProgressionDialog = useCallback(() => {
    setPhaseProgressionDialogOpen(false);
  }, []);

  const handlePhaseProgressionSectionChange = useCallback((sectionKey) => (_event, expanded) => {
    if (expanded) {
      setPhaseProgressionSection(sectionKey);
    }
  }, []);

  const handlePhaseTransitionSelect = useCallback((event) => {
    const nextKey = event.target.value;
    setSelectedPhaseTransitionKey(nextKey);
    const transition = (phaseProgressionData?.transitions || []).find((item) => getPhaseTransitionKey(item) === nextKey) || null;
    seedPhaseProgressionDraft(transition);
  }, [getPhaseTransitionKey, phaseProgressionData, seedPhaseProgressionDraft]);

  const handlePhaseConditionChange = useCallback((comboLocalId, conditionIndex, field, value) => {
    setPhaseRuleDraftCombos((current) => current.map((combo) => {
      if (combo.localId !== comboLocalId) {
        return combo;
      }

      return {
        ...combo,
        conditions: combo.conditions.map((condition, index) => {
          if (index !== conditionIndex) return condition;
          if (field === 'metric_key') {
            const nextMetric = phaseProgressionMetricOptions.find((option) => option.key === value) || phaseProgressionMetricOptions[0] || PHASE_PROGRESSION_METRIC_OPTIONS[0];
            return {
              ...condition,
              metric_key: nextMetric.key,
              operator: nextMetric.defaultOperator,
              metric_source: nextMetric.metricSource || '',
              kpi_type: nextMetric.kpiType || '',
              kpi_subtype: nextMetric.kpiSubtype || '',
              question_identifier: nextMetric.questionIdentifier || '',
              exercise_identifiers_text: nextMetric.key === 'exercise_level_progressions' ? condition.exercise_identifiers_text : '',
            };
          }
          return { ...condition, [field]: value };
        }),
      };
    }));
  }, [phaseProgressionMetricOptions]);

  const handlePhaseComboJoinOperatorChange = useCallback((comboLocalId, value) => {
    setPhaseRuleDraftCombos((current) => current.map((combo, index) => {
      if (combo.localId !== comboLocalId || index === 0) {
        return combo;
      }
      return {
        ...combo,
        combo_join_operator: value === 'and' ? 'and' : 'or',
      };
    }));
  }, []);

  const handleAddPhaseCondition = useCallback((comboLocalId) => {
    setPhaseRuleDraftCombos((current) => current.map((combo) => {
      if (combo.localId !== comboLocalId || combo.conditions.length >= 3) {
        return combo;
      }

      return {
        ...combo,
        conditions: [
          ...combo.conditions,
          buildPhaseProgressionConditionDraft(
            getNextPhaseProgressionMetricKey(combo.conditions, combo.conditions.length, phaseProgressionMetricOptions),
            {},
            phaseProgressionMetricOptions,
          )
        ],
      };
    }));
  }, [phaseProgressionMetricOptions]);

  const handleRemovePhaseCondition = useCallback((comboLocalId, conditionIndex) => {
    setPhaseRuleDraftCombos((current) => current.map((combo) => {
      if (combo.localId !== comboLocalId || combo.conditions.length <= 1 || conditionIndex === 0) {
        return combo;
      }

      return {
        ...combo,
        conditions: combo.conditions.filter((_condition, index) => index !== conditionIndex),
      };
    }));
  }, []);

  const handleAddPhaseConditionCombo = useCallback(() => {
    setPhaseRuleDraftCombos((current) => [
      ...current,
      buildDefaultPhaseProgressionConditionCombo(phaseProgressionMetricOptions, 'or'),
    ]);
  }, [phaseProgressionMetricOptions]);

  const handleRemovePhaseConditionCombo = useCallback((comboLocalId) => {
    setPhaseRuleDraftCombos((current) => {
      if (current.length <= 1) {
        return current;
      }
      return current.filter((combo) => combo.localId !== comboLocalId);
    });
  }, []);

  const handleSavePhaseProgressionRule = useCallback(async () => {
    if (!selectedTreatmentId || !token || !selectedPhaseTransition) return;

    const conditions = [];
    for (const [comboIndex, combo] of phaseRuleDraftCombos.entries()) {
      const firstCondition = combo.conditions[0];
      if (!firstCondition || !PHASE_PROGRESSION_TIME_METRIC_KEYS.includes(firstCondition.metric_key)) {
        setSnackbarMsg(`Rule combo ${comboIndex + 1} must start with Sessions completed or Duration in phase (weeks).`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const selectedMetricKeys = combo.conditions.map((condition) => condition.metric_key).filter(Boolean);
      if (new Set(selectedMetricKeys).size !== selectedMetricKeys.length) {
        setSnackbarMsg(`Rule combo ${comboIndex + 1} cannot contain duplicate metrics.`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      for (const condition of combo.conditions) {
        const thresholdValue = Number(condition.threshold_value);
        if (!Number.isFinite(thresholdValue)) {
          setSnackbarMsg(`Rule combo ${comboIndex + 1} needs numeric thresholds for every criterion.`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
        }

        const payloadCondition = {
          metric_key: condition.metric_key,
          operator: condition.operator || 'gte',
          threshold_value: thresholdValue,
          combo_index: comboIndex,
          combo_join_operator: comboIndex === 0 ? 'and' : combo.combo_join_operator,
        };

        if (condition.metric_source) payloadCondition.metric_source = condition.metric_source;
        if (condition.kpi_type) payloadCondition.kpi_type = condition.kpi_type;
        if (condition.kpi_subtype) payloadCondition.kpi_subtype = condition.kpi_subtype;
        if (condition.question_identifier) payloadCondition.question_identifier = condition.question_identifier;

        if (condition.metric_key === 'exercise_level_progressions') {
          const exerciseIdentifiers = (condition.exercise_identifiers_text || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
          if (exerciseIdentifiers.length > 0) {
            payloadCondition.exercise_identifiers = exerciseIdentifiers;
          }
        }

        conditions.push(payloadCondition);
      }
    }

    setPhaseProgressionSaving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/treatments/${selectedTreatmentId}/phase-progression-rules/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_phase_index: selectedPhaseTransition.source_phase_index,
          target_phase_index: selectedPhaseTransition.target_phase_index,
          conditions,
          apply_to_clinician_defaults: rememberPhaseRuleForAllPatients,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || 'Unable to save phase progression rule.');
      }
      const data = await res.json();
      setPhaseProgressionData(data);
      const nextTransition = (data.transitions || []).find((transition) => getPhaseTransitionKey(transition) === selectedPhaseTransitionKey) || activePhaseTransition;
      if (nextTransition) {
        seedPhaseProgressionDraft(nextTransition, normalizePhaseProgressionMetricOptions(data.available_metrics));
      }
      setSnackbarMsg(rememberPhaseRuleForAllPatients ? 'Phase progression rule saved for this treatment and clinician template.' : 'Phase progression rule saved for this treatment.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMsg(error.message || 'Unable to save phase progression rule.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setPhaseProgressionSaving(false);
  }, [activePhaseTransition, getPhaseTransitionKey, phaseRuleDraftCombos, rememberPhaseRuleForAllPatients, seedPhaseProgressionDraft, selectedPhaseTransition, selectedPhaseTransitionKey, selectedTreatmentId, token]);

  const handleClearPhaseProgressionOverride = useCallback(async () => {
    if (!selectedTreatmentId || !token || !selectedPhaseTransition) return;

    setPhaseProgressionSaving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/treatments/${selectedTreatmentId}/phase-progression-rules/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_phase_index: selectedPhaseTransition.source_phase_index,
          target_phase_index: selectedPhaseTransition.target_phase_index,
          clear_override: true,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || 'Unable to clear the patient override.');
      }
      const data = await res.json();
      setPhaseProgressionData(data);
      const nextTransition = (data.transitions || []).find((transition) => getPhaseTransitionKey(transition) === selectedPhaseTransitionKey) || activePhaseTransition;
      if (nextTransition) {
        seedPhaseProgressionDraft(nextTransition);
      }
      setSnackbarMsg('Patient phase progression override cleared.');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMsg(error.message || 'Unable to clear the patient override.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setPhaseProgressionSaving(false);
  }, [activePhaseTransition, getPhaseTransitionKey, seedPhaseProgressionDraft, selectedPhaseTransition, selectedPhaseTransitionKey, selectedTreatmentId, token]);

  const handleManualPhasePromotion = useCallback(async () => {
    if (!selectedTreatmentId || !token || !phasePromotionTarget) return;

    setPhasePromotionLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/treatments/${selectedTreatmentId}/phase-progression-promote/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_phase_index: Number(phasePromotionTarget),
          rationale: phasePromotionRationale,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || 'Unable to change to the selected phase.');
      }
      const data = await res.json();
      setPhaseProgressionData(data.progression_rules || null);
      if (Array.isArray(phases) && typeof setSelectedPhaseId === 'function') {
        const matchingPhase = phases.find((phase) => String(phase.index ?? phase.phaseIndex) === String(data.active_phase_index));
        if (matchingPhase?.id) {
          setSelectedPhaseId(matchingPhase.id);
        }
      }
      if (selectedPatient?.user_id && typeof fetchPatientAllData === 'function') {
        const diagId = selectedPatient.selected_diagnosis_id || diagnosis?.id || diagnosis?.diagnosis_id || null;
        await fetchPatientAllData(selectedPatient.user_id, diagId, selectedTreatmentId);
      }
      if (typeof refreshPatientTableData === 'function') {
        await refreshPatientTableData();
      }
      setSnackbarMsg(`Patient moved to ${data.active_phase_name || `phase ${data.active_phase_index}`}.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setPhasePromotionRationale('');
    } catch (error) {
      setSnackbarMsg(error.message || 'Unable to change to the selected phase.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setPhasePromotionLoading(false);
  }, [diagnosis?.diagnosis_id, diagnosis?.id, fetchPatientAllData, phasePromotionRationale, phasePromotionTarget, phases, refreshPatientTableData, selectedPatient?.selected_diagnosis_id, selectedPatient?.user_id, selectedTreatmentId, setSelectedPhaseId, token]);

  const handleAcceptSuggestion = async (adjustmentId) => {
    if (!token) return;
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/exercise-adjustment/${adjustmentId}/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'accept' }),
      });
      if (res.ok) {
        const data = await res.json();
        setSnackbarMsg(`Exercise replaced: ${data.original_title || data.original_identifier} → ${data.replacement_title || data.replacement_identifier}.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchPendingSuggestions();
        if (selectedPhaseId && typeof fetchExercisesForPhase === 'function') fetchExercisesForPhase(selectedPhaseId);
      }
    } catch (err) { console.warn('Failed to accept suggestion', err); }
  };

  const handleRejectSuggestion = async (adjustmentId) => {
    if (!token) return;
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/exercise-adjustment/${adjustmentId}/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reject' }),
      });
      if (res.ok) {
        setSnackbarMsg('Suggestion rejected.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        fetchPendingSuggestions();
      }
    } catch (err) { console.warn('Failed to reject suggestion', err); }
  };

  // Bulk-edit state
  const bulkEditParamDefaults = { sets: 3, repetitions: 10, repetitionMaximum: 1, weightKg: 0, weightTotalKg: 0, weightLbs: 0, weightTotalLbs: 0, time: 30, doseType: 'time', pause: 15, frequency: 1, frequencyType: 'daily', daysInbetween: 1 };
  const [bulkEditParam, setBulkEditParam] = useState('sets');
  const [bulkEditValue, setBulkEditValue] = useState(3);

  // Edit exercise dialog state
  const [editExerciseDialogOpen, setEditExerciseDialogOpen] = useState(false);
  const [selectedExerciseToEdit, setSelectedExerciseToEdit] = useState(null);
  const [editedExerciseParams, setEditedExerciseParams] = useState({
    sets: 3,
    repetitions: 10,
    repetitionMaximum: 1,
    weightKg: 0,
    weightTotalKg: 0,
    weightLbs: 0,
    weightTotalLbs: 0,
    time: 30,
    pause: 15,
    frequencyType: "daily",
    frequency: 1,
    daysInbetween: '',
    description: "",
    title: "",
    doseType: "time",
  });

  // Edit advice dialog state
  const [editAdviceDialogOpen, setEditAdviceDialogOpen] = useState(false);
  const [selectedAdviceToEdit, setSelectedAdviceToEdit] = useState(null);
  const [libraryDrawerOpen, setLibraryDrawerOpen] = useState(false);
  const [libBtnHovered, setLibBtnHovered] = useState(false);

  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMode, setReportMode] = useState('create'); // 'create' or 'view'
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusReports, setStatusReports] = useState([]);
  const [epicrisisReports, setEpicrisisReports] = useState([]);
  const [reportStats, setReportStats] = useState(null);

  // Store journal sections and diagnosis from SelectedPatientDiagnosis for epicrisis generation
  const [currentJournalSections, setCurrentJournalSections] = useState(null);
  const [currentAppliedDiagnosis, setCurrentAppliedDiagnosis] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!reportDialogOpen) {
        setReportStats(null);
        return;
      }
      if (!selectedPatient || !selectedPatient.user_id || !token) {
        setReportStats(null);
        return;
      }

      const treatmentId = selectedPatient.treatment_id || props.treatmentId || (activeDiagnosis && activeDiagnosis.treatment_id) || null;
      const diagIdentifier = diagnosisIdentifierForReport;

      const assessmentDateIso = assessmentDate && (assessmentDate.assessment_date || assessmentDate.date || assessmentDate.assessmentDate);
      let timeInTreatmentDays = null;
      if (assessmentDateIso) {
        const start = new Date(assessmentDateIso);
        if (!Number.isNaN(start.getTime())) {
          timeInTreatmentDays = Math.max(1, Math.ceil((Date.now() - start.getTime()) / 86400000));
        }
      }

      let phaseName = null;
      let phaseProgress = null;

      if (diagIdentifier && treatmentId) {
        try {
          const phaseRes = await fetch(`${apiBaseUrl}/treatment/phase/progress/user/${selectedPatient.user_id}/${diagIdentifier}/?treatment_id=${treatmentId}`, {
            headers: { 'Authorization': `Token ${token}` },
            method: 'GET',
          });
          if (phaseRes.ok) {
            const phaseData = await phaseRes.json();
            phaseProgress = (phaseData && phaseData.progress !== undefined) ? phaseData.progress : null;
            phaseName = phaseData && phaseData.name ? phaseData.name : null;
          }
        } catch (e) {
          // ignore
        }
      }

      if (!phaseName && Array.isArray(phases) && selectedPhaseId) {
        const p = phases.find(ph => String(ph.id) === String(selectedPhaseId));
        if (p && p.name) phaseName = p.name;
      }

      let totalSessions = null;
      let last7Sessions = null;
      let plannedSessionsPerDay = null;
      let avgSessionsPerDay = null;
      let last7SessionsPerDay = null;
      let complianceOverall = null;
      let complianceLast7 = null;
      let avgPainOverall = null;
      let avgPainLast7 = null;

      if (diagIdentifier && treatmentId) {
        try {
          const sessionsRes = await fetch(`${apiBaseUrl}/treatment/sessions/all/${selectedPatient.user_id}/${diagIdentifier}/?treatment_id=${treatmentId}`, {
            headers: { 'Authorization': `Token ${token}` },
            method: 'GET',
          });
          if (sessionsRes.ok) {
            const allSessionsData = await sessionsRes.json();
            const sessions = Array.isArray(allSessionsData.sessions) ? allSessionsData.sessions : [];
            totalSessions = sessions.length;
            const now = Date.now();
            const last7Start = now - (7 * 24 * 60 * 60 * 1000);
            const sessionsLast7 = sessions.filter(s => {
              const t = new Date(s.completed_date).getTime();
              return !Number.isNaN(t) && t >= last7Start;
            });
            last7Sessions = sessionsLast7.length;
            
            // Calculate average pain per exercise from all sessions (overall)
            const painLevelsAll = [];
            sessions.forEach(s => {
              if (Array.isArray(s.exercise_feedback)) {
                s.exercise_feedback.forEach(ex => {
                  if (ex.painLevel !== null && ex.painLevel !== undefined && !Number.isNaN(Number(ex.painLevel))) {
                    painLevelsAll.push(Number(ex.painLevel));
                  }
                });
              }
            });
            if (painLevelsAll.length > 0) {
              avgPainOverall = painLevelsAll.reduce((sum, val) => sum + val, 0) / painLevelsAll.length;
            }
            
            // Calculate average pain per exercise from last 7 days sessions
            const painLevelsLast7 = [];
            sessionsLast7.forEach(s => {
              if (Array.isArray(s.exercise_feedback)) {
                s.exercise_feedback.forEach(ex => {
                  if (ex.painLevel !== null && ex.painLevel !== undefined && !Number.isNaN(Number(ex.painLevel))) {
                    painLevelsLast7.push(Number(ex.painLevel));
                  }
                });
              }
            });
            if (painLevelsLast7.length > 0) {
              avgPainLast7 = painLevelsLast7.reduce((sum, val) => sum + val, 0) / painLevelsLast7.length;
            }
            console.log('[SelectedPatientProgram] Pain calculation - overall:', painLevelsAll, 'avgPainOverall:', avgPainOverall, 'last7:', painLevelsLast7, 'avgPainLast7:', avgPainLast7);
            plannedSessionsPerDay = (typeof allSessionsData.planned_sessions_per_day_overall === 'number' && allSessionsData.planned_sessions_per_day_overall > 0)
              ? Number(allSessionsData.planned_sessions_per_day_overall)
              : 3;
            if (timeInTreatmentDays) {
              avgSessionsPerDay = totalSessions / timeInTreatmentDays;
              complianceOverall = plannedSessionsPerDay ? (avgSessionsPerDay / plannedSessionsPerDay) * 100 : null;
            }
            last7SessionsPerDay = last7Sessions / 7;
            complianceLast7 = plannedSessionsPerDay ? (last7SessionsPerDay / plannedSessionsPerDay) * 100 : null;
          }
        } catch (e) {
          // ignore
        }
      }

      if (cancelled) return;
      const statsToSet = {
        timeInTreatmentDays,
        assessmentStartDate: assessmentDateIso,
        phaseName,
        phaseProgress,
        totalSessions,
        last7Sessions,
        plannedSessionsPerDay,
        avgSessionsPerDay,
        last7SessionsPerDay,
        complianceOverall,
        complianceLast7,
        avgPainOverall,
        avgPainLast7,
        currentExercisesCount: Array.isArray(exercises) ? exercises.length : 0
      };
      console.log('[SelectedPatientProgram] Setting reportStats:', statsToSet);
      console.log('[SelectedPatientProgram] Context:', {
        userId: selectedPatient?.user_id,
        treatmentId,
        diagIdentifier,
        assessmentDateIso,
        timeInTreatmentDays,
        totalSessions,
        phaseName
      });
      setReportStats(statsToSet);
    };
    run();
    return () => { cancelled = true; };
  }, [reportDialogOpen, selectedPatient?.user_id, selectedPatient?.treatment_id, token, diagnosisIdentifierForReport, assessmentDate, phases, selectedPhaseId, activeDiagnosis, exercises]);

  const [editedAdviceParams, setEditedAdviceParams] = useState({
    title: "",
    advice: "",
    reason: "",
    importance: 1,
  });
  

  // Remove advice handler (calls PATCH to backend)
  const handleRemoveAdvice = async (adviceId) => {
    if (!selectedPatient || !selectedPhaseId) return;
    // Remove from UI and patch using the fresh list to avoid stale closures
    setAdvicesForSelectedPhase(prev => {
      const newAdvices = prev.filter(a => a.id !== adviceId);
      setEditableAdvices(newAdvices);
      // Include treatment_id in query params for treatment-scoped fetching
      const adviceParams = new URLSearchParams();
      if (props.treatmentId) {
        adviceParams.append('treatment_id', props.treatmentId);
      }
      const adviceUrl = `${apiBaseUrl}/treatment/advices/patient/${selectedPatient.user_id}/?${adviceParams.toString()}`;
      fetch(adviceUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify({ phase_id: String(selectedPhaseId), advices: newAdvices })
      }).catch(() => {});
      return newAdvices;
    });
  };

  // Open exercise edit dialog
  const handleOpenEditExerciseDialog = (exercise) => {
    setSelectedExerciseToEdit(exercise);
    setEditedExerciseParams(buildExerciseEditParams(exercise));
    setEditExerciseDialogOpen(true);
  };

  // Handler to create a brand new custom exercise from scratch
  const handleCreateCustomExercise = () => {
    setSelectedExerciseToEdit({
      title: "",
      description: "",
      img: null,
      video: null,
      exerciseType: 'custom',
      identifier: null, // No identifier means it's a custom exercise
      isNewCustomExercise: true,
    });
    setEditedExerciseParams({
      sets: 3,
      repetitions: 10,
      time: 30,
      pause: 15,
      frequencyType: "daily",
      frequency: 1,
      daysInbetween: '',
      description: "",
      title: "",
      doseType: "time",
      img: '',
      video: '',
      patientInMedia: null,
    });
    setEditExerciseDialogOpen(true);
  };

  const handleCloseEditExerciseDialog = () => {
    setEditExerciseDialogOpen(false);
    setSelectedExerciseToEdit(null);
  };

  const handleEditExerciseParamChange = (field, value) => {
    if (field === "time" || field === "pause" || field === "daysInbetween") {
      setEditedExerciseParams(prev => ({ ...prev, [field]: parseInt(value, 10) || 0 }));
    } else if (field === "doseType") {
      // When switching dose type, clear the unused field to keep exactly 6 active inputs
      setEditedExerciseParams(prev => (
        field === "doseType" && value === "repetitions"
          ? { ...prev, doseType: value, time: 0 }
          : { ...prev, doseType: value, repetitions: 0 }
      ));
    } else if (field === "frequencyType") {
      setEditedExerciseParams(prev => ({
        ...prev,
        frequencyType: value,
        daysInbetween: value === 'weekly' ? (prev.daysInbetween || prev.frequency || 1) : '',
      }));
    } else {
      setEditedExerciseParams(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleConfirmEditExercise = () => {
    if (!selectedExerciseToEdit) return;

    // Normalize time/repetitions based on chosen dose type
    const normalizedParams = normalizeEditedExerciseParams(editedExerciseParams);
    
    // If the exercise has no id and no uniqueKey, it's a brand new custom exercise
    if (!selectedExerciseToEdit.id && !selectedExerciseToEdit.uniqueKey) {
      // Add as new custom exercise
      const newCustomExercise = {
        ...normalizedParams,
        uniqueKey: uuidv4(),
        identifier: null, // Custom exercise has no identifier
        exerciseType: selectedExerciseToEdit.exerciseType || 'custom',
        index: exercisesState.length + 1,
        img: normalizedParams.img || selectedExerciseToEdit.img || null,
        video: normalizedParams.video || selectedExerciseToEdit.video || null,
      };
      setExercisesState(prev => [...prev, newCustomExercise]);
    } else {
      // Update existing exercise
      setExercisesState(prev =>
        prev.map(ex =>
          (selectedExerciseToEdit.id && ex.id === selectedExerciseToEdit.id) || 
          (!selectedExerciseToEdit.id && ex.uniqueKey === selectedExerciseToEdit.uniqueKey)
            ? { ...ex, ...normalizedParams }
            : ex
        )
      );
    }
    setEditExerciseDialogOpen(false);
    setSelectedExerciseToEdit(null);
  };

  // Open advice edit dialog
  const handleOpenEditAdviceDialog = (advice) => {
    setSelectedAdviceToEdit(advice);
    setEditedAdviceParams({
      title: advice.title || "",
      advice: advice.advice || "",
      reason: "",
      importance: advice.importance || 1,
    });
    setEditAdviceDialogOpen(true);
  };

  const handleCloseEditAdviceDialog = () => {
    setEditAdviceDialogOpen(false);
    setSelectedAdviceToEdit(null);
  };

  const handleEditAdviceParamChange = (field, value) => {
    setEditedAdviceParams(prev => ({ ...prev, [field]: value }));
  };

  const handleConfirmEditAdvice = async () => {
    if (!selectedAdviceToEdit || !selectedPatient) return;
    if (!selectedAdviceToEdit.id) {
      setSnackbarMsg('Save this advice first, then customize it.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await fetch(
        `${apiBaseUrl}/treatment/advices/patient/${selectedPatient.user_id}/customize/`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            advice_id: selectedAdviceToEdit.id,
            custom_title: editedAdviceParams.title,
            content: editedAdviceParams.advice,
            reason: editedAdviceParams.reason,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to customize advice');
      }

      // Update local state with customized advice
      setEditableAdvices(prev =>
        prev.map(a =>
          a.id === selectedAdviceToEdit.id
            ? {
                ...a,
                title: editedAdviceParams.title || a.title,
                advice: editedAdviceParams.advice,
                importance: editedAdviceParams.importance,
                reason: editedAdviceParams.reason || a.reason,
                is_customized: true,
                customized_at: new Date().toISOString(),
              }
            : a
        )
      );

      // Also notify parent about importance change
      if (editedAdviceParams.importance !== selectedAdviceToEdit.importance) {
        handleUpdateAdviceImportance(
          { ...selectedAdviceToEdit, importance: editedAdviceParams.importance },
          editedAdviceParams.importance
        );
      }

      setSnackbarMsg('Advice customized successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setEditAdviceDialogOpen(false);
      setSelectedAdviceToEdit(null);
    } catch (error) {
      console.error('Error customizing advice:', error);
      setSnackbarMsg('Failed to customize advice');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // leftTab is now controlled by parent via selectedPhaseId/setSelectedPhaseId
  // Use uniqueKey for every exercise instance
  const [exercisesState, setExercisesState] = useState([]); // local copy for UI edits
  // Update local state when exercises prop changes (including clearing on treatment change)
  useEffect(() => {
  // console.log('[selectedPatientProgram] Exercises prop changed, updating local state. Count:', exercises.length, 'Treatment:', props.treatmentId);
  setExercisesState(
    exercises.map(ex => ({
      ...ex,
      uniqueKey: ex.id ? ex.id : uuidv4(),
      sets: ex.sets ?? 3,
      repetitions: ex.repetitions ?? 10,
      time: ex.time ?? "30_seconds",
      pause: ex.pause ?? "15_seconds",
      frequencyType: normalizeScheduleType(ex.frequencyType ?? "daily"),
      frequency: ex.frequency ?? 1,
      daysInbetween: normalizeScheduleType(ex.frequencyType ?? 'daily') === 'weekly'
        ? (ex.daysInbetween ?? ex.daysInBetween ?? ex.frequency ?? 1)
        : '',
      index: ex.index ?? 1,
      doseType: ex.doseType || ((ex.time && ex.time !== 0) ? "time" : "repetitions"),
      description: ex.description ?? "",
      title: ex.title ?? "",
      img: ex.img || ex.picture || ex.image_url || null,
      video: ex.video || ex.video_url || null,
      is_paused: Boolean(ex.is_paused),
    }))
  );
  }, [selectedPatient, exercises, props.treatmentId]);

  // Fetch the latest diagnosis for the selected patient (profile-scoped). Prefer consolidated prop if provided.
  useEffect(() => {
    let cancelled = false;
    async function fetchLatestDiagnosis() {
      if (!selectedPatient) {
        setLatestDiagnosis(null);
        return;
      }

      // Use pre-fetched list if available from parent
      if (Array.isArray(allDiagnoses) && allDiagnoses.length > 0) {
        setLatestDiagnosis(allDiagnoses[allDiagnoses.length - 1]);
        return;
      }

      const profileId = selectedPatient.user_id || selectedPatient.id;
      if (!profileId) {
        setLatestDiagnosis(null);
        return;
      }

      try {
        const resp = await fetch(`${apiBaseUrl}/treatment/all_diagnosis/${profileId}/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`
          }
        });
        if (!resp.ok) {
          setLatestDiagnosis(null);
          return;
        }
        const data = await resp.json();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setLatestDiagnosis(data[data.length - 1]);
        } else {
          setLatestDiagnosis(null);
        }
      } catch (err) {
        console.error('Failed to fetch diagnoses for profile', profileId, err);
        if (!cancelled) setLatestDiagnosis(null);
      }
    }
    fetchLatestDiagnosis();
    return () => { cancelled = true; };
  }, [selectedPatient?.user_id, allDiagnoses]);
  // Fetch active session status once on patient change (no polling). Prefer consolidated sessionData if provided.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedPatient || !selectedPatient.user_id) { setActiveSessionInfo(null); setActiveSessionLocked(false); return; }

      // Use pre-fetched session info from parent if available
      if (sessionData) {
        if (sessionData.active) {
          setActiveSessionInfo(sessionData);
          setActiveSessionLocked(true);
        } else {
          setActiveSessionInfo(null);
          setActiveSessionLocked(false);
        }
        return;
      }

      const status = await fetchActiveSessionStatus(selectedPatient.user_id, token);
      if (cancelled) return;
      if (status?.active) {
        setActiveSessionInfo(status);
        setActiveSessionLocked(true);
      } else {
        setActiveSessionInfo(null);
        setActiveSessionLocked(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedPatient?.user_id, sessionData]);

  // Helper to fetch active session status; returns {active, session_id, started_date, phase, phase_index} or null
  const fetchActiveSessionStatus = async (userId, authToken) => {
    try {
      const res = await fetch(`${apiBaseUrl}/treatment/session/active/${userId}/`, {
        method: 'GET',
        headers: { 'Authorization': `Token ${authToken}`, 'Accept': 'application/json' }
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data && data.active) {
        return {
          active: true,
          session_id: data.session_id,
          started_date: data.started_date,
          phase: data.phase,
          phase_index: data.phase_index
        };
      }
      return { active: false };
    } catch (e) {
      return null;
    }
  };
  const originalExerciseIds = exercises.filter(ex => ex.id).map(ex => ex.id);
  const { innerWidth: width, innerHeight: height } = window;
  //const [exercises, setExercises] = useState([]);
  //const [selectedPatientExercises, setSelectedPatientExercises] = useState([]);
  const skeletonArray = Array.from({ length: 5 });
  let didCancel = false;
  

  // Utility to parse seconds from string or number
function parseSeconds(val) {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const match = val.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  }
  return 0;
}

// Add exercise to local state, prevent duplicates by id
  const handleAddExercise = (exercise) => {
    // Prevent duplicates by identifier when available (covers library exercises)
    const identifier = exercise.identifier || null;
    if (identifier && exercisesState.some(e => e.identifier === identifier)) return;
    // If exercise has a patient-specific id already in state, don't add again
    if (exercise.id && exercisesState.some(e => e.id === exercise.id)) return;

    // Treat library exercises as NEW entries (do not carry id) so bulk_add runs
    const uniqueKey = uuidv4();
    setExercisesState(prev => [...prev, {
      ...exercise,
      id: null,
      libraryExerciseId: exercise.id || null,
      uniqueKey,
      index: prev.length + 1,
      doseType: exercise.doseType || ((exercise.time && exercise.time !== 0) ? "time" : "repetitions"),
    }]);
  };

 // Remove from UI immediately for both saved and unsaved exercises
  const handleRemoveExercise = (exercise) => {
    setExercisesState(prev => prev.filter(e => {
      if (exercise.id) {
        return e.id !== exercise.id;
      } else {
        return e.uniqueKey !== exercise.uniqueKey;
      }
    }));
  };

  // Helper to resolve treatment_id from selectedPatient or available treatments
  const resolveTreatmentId = useCallback(() => {
    if (!selectedPatient) {
      console.warn('[resolveTreatmentId] selectedPatient is null/undefined');
      return null;
    }
    
    // Priority 1: Use explicit treatment_id if set
    if (selectedPatient.treatment_id) {
      console.log('[resolveTreatmentId] Using explicit treatment_id:', selectedPatient.treatment_id);
      return selectedPatient.treatment_id;
    }
    
    // Priority 2: Use nested treatment object if available
    if (selectedPatient.treatment?.id) {
      console.log('[resolveTreatmentId] Using treatment.id:', selectedPatient.treatment.id);
      return selectedPatient.treatment.id;
    }
    
    // Priority 3: Auto-select from available treatments (single or latest)
    if (Array.isArray(selectedPatient.treatments) && selectedPatient.treatments.length > 0) {
      if (selectedPatient.treatments.length === 1) {
        const tid = selectedPatient.treatments[0].id || selectedPatient.treatments[0].treatment_id;
        console.log('[resolveTreatmentId] Single treatment, using:', tid);
        return tid;
      }
      // Multiple: return latest
      const sorted = [...selectedPatient.treatments].sort((a, b) => {
        const aTime = new Date(a.created_at || a.createdAt || 0).getTime();
        const bTime = new Date(b.created_at || b.createdAt || 0).getTime();
        return bTime - aTime;
      });
      const tid = sorted[0].id || sorted[0].treatment_id;
      console.log('[resolveTreatmentId] Multiple treatments, using latest:', tid);
      return tid;
    }
    
    // Priority 4: Try to use diagnosis.treatment_id (from diagnosis object)
    if (selectedPatient.diagnosis && Array.isArray(selectedPatient.diagnosis) && selectedPatient.diagnosis.length > 0) {
      const latestDiag = selectedPatient.diagnosis[selectedPatient.diagnosis.length - 1];
      if (latestDiag?.treatment_id) {
        console.log('[resolveTreatmentId] Using diagnosis.treatment_id:', latestDiag.treatment_id);
        return latestDiag.treatment_id;
      }
    }
    
    console.warn('[resolveTreatmentId] No treatment found', {
      treatment_id: selectedPatient.treatment_id,
      treatment: selectedPatient.treatment,
      treatments: selectedPatient.treatments,
      diagnosis: selectedPatient.diagnosis
    });
    return null;
  }, [selectedPatient]);

  // Helper to update or remove exercise
  const handleEditExercise = async (exercise, action, updatedFields = {}) => {
    if (!selectedPatient) return;
    try {
      const treatmentId = resolveTreatmentId();
      if (!treatmentId) {
        console.error('No treatment_id found for patient');
        setSnackbarMsg('Error: Unable to resolve treatment for this patient');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      const response = await fetch(`${apiBaseUrl}/treatment/exercise/edit/${selectedPatient.user_id}/${treatmentId}/`, {
        // ...implementation...
      });
    } catch (error) {
      console.error(error);
    }
  };
  // Save all exercises handler
  const handleSaveAllExercises = async () => {
    // Preflight: block save if there's an active session
    if (selectedPatient && selectedPatient.user_id) {
      const status = await fetchActiveSessionStatus(selectedPatient.user_id, token);
      if (status && status.active) {
        setActiveSessionInfo(status);
        setActiveSessionLocked(true);
        setSnackbarMsg('Patient has an active session running. Please retry after it ends.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      } else {
        setActiveSessionLocked(false);
      }
    }
    
    const treatmentId = resolveTreatmentId();
    if (!treatmentId) {
      setSnackbarMsg('Error: Unable to resolve treatment for this patient');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // CRITICAL: Ensure we have a valid phase_id to save to
    if (!selectedPhaseId) {
      setSnackbarMsg('Error: No phase selected');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    console.log("Saving to phase:", selectedPhaseId);
    console.log("Original exercises:", originalExerciseIds);
    try {
      setIsLoading(true);
      const saveResult = await persistExerciseProgramChanges({
        originalExercises: exercises,
        nextExercises: exercisesState,
        userId: selectedPatient.user_id,
        treatmentId,
        phaseId: selectedPhaseId,
        token,
        changeSource: 'program',
      });
      // Refresh exercises from backend (if provided)
      if (typeof fetchExercisesForPatient === 'function') {
        try {
          await fetchExercisesForPatient();
        } catch (err) {
          // Non-fatal: still consider save successful, but log
          console.warn('Failed to refresh exercises after save:', err);
        }
      }
      setSnackbarMsg('All changes saved!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      if (saveResult.changeEvent && saveResult.changeSummary?.journalLines?.length) {
        setProgramJournalConfirmation(saveResult.changeEvent);
      }
    } catch (error) {
      console.error('Error saving exercises:', error);
      setSnackbarMsg('Error saving exercises: ' + (error && error.message ? error.message : 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmProgramJournal = () => {
    if (!programJournalConfirmation) return;
    confirmExerciseProgramUpdate(programJournalConfirmation);
    setProgramJournalConfirmation(null);
    setActiveTab('journal');
  };

  const handleReviewProgramJournal = () => {
    if (programJournalConfirmation) {
      reviewExerciseProgramUpdate(programJournalConfirmation);
    }
    setProgramJournalConfirmation(null);
    setActiveTab('journal');
  };

//useEffect(() => {
//  console.log("exercisesState changed:", exercisesState);
//}, [exercisesState]);
//
//useEffect(() => {
//  console.log("Received exercises prop:", exercises);
//}, [exercises]);

// currentPhase logic is now handled in the parent; child only uses selectedPhaseId

// Remove any default/placeholder logic for dropdown selection in the child
// Ensure dropdown value is always set to selectedPhaseId from parent

// When the phase dropdown changes, update parent and fetch exercises for that phase
const handlePhaseDropdownChange = (e) => {
  const value = e.target.value;
  setSelectedPhaseId(value); // Parent wrapper marks this as user action
  // Find the selected phase object
  const selectedPhase = exercisePhases.find(opt => opt.value === value);
  if (selectedPhase && selectedPhase.phaseId && fetchExercisesForPhase) {
    fetchExercisesForPhase(selectedPhase.phaseId); // Pass phaseId directly
  }
};

// When clicking the Program tab, refresh exercises for the current phase
function handleProgramTabClick() {
  if (!canAdaptiveExercise) {
    setActiveTab('Overview');
    return;
  }
  setActiveTab('exerciseProgram');
  if (selectedPhaseId && typeof fetchExercisesForPhase === 'function') {
    const phaseExists = exercisePhases.some(phase => phase.value === selectedPhaseId);
    if (phaseExists) {
      fetchExercisesForPhase(selectedPhaseId);
    }
  }
}
 
// Refresh the exercises only after a feedback is submitted (event-driven)
// The patient feedback UI should dispatch: window.dispatchEvent(new CustomEvent('exerciseFeedbackSubmitted'))
useEffect(() => {
  if (typeof fetchExercisesForPhase !== 'function' || !selectedPhaseId || !exercisePhases.length) return;
  
  // Validate that the selected phase exists before fetching
  const phaseExists = exercisePhases.some(phase => phase.value === selectedPhaseId);
  if (!phaseExists) {
    // Don't warn here - let the phase auto-selection effect handle resetting invalid phases
    return;
  }
  
  const onFeedbackSubmitted = () => {
    try {
      fetchExercisesForPhase(selectedPhaseId);
    } catch (err) {
      console.warn('Failed to refresh exercises after feedback:', err);
    }
  };
  window.addEventListener('exerciseFeedbackSubmitted', onFeedbackSubmitted);
  return () => window.removeEventListener('exerciseFeedbackSubmitted', onFeedbackSubmitted);
}, [selectedPhaseId, fetchExercisesForPhase, exercisePhases]);

// --- Advice fetching and filtering logic ---
const [allPhaseAdvices, setAllPhaseAdvices] = useState([]);
const [advicesForSelectedPhase, setAdvicesForSelectedPhase] = useState([]);
const [currentPhaseNameForAdvices, setCurrentPhaseNameForAdvices] = useState('');
const [editableAdvices, setEditableAdvices] = useState([]);

// Fetch advices: prioritize prop, fallback to fetch
useEffect(() => {
  const currentTreatmentId = props.treatmentId || null;
  const treatmentChanged = currentTreatmentId !== lastAdvicesTreatmentRef.current;
  
  if (!selectedPatient || !selectedPatient.user_id) {
    setAllPhaseAdvices([]);
    lastAdvicesTreatmentRef.current = null;
    return;
  }

  // If treatment hasn't changed and we have advices prop, use it
  if (!treatmentChanged && advices) {
    const data = advices;
    if (Array.isArray(data.all_advices)) {
      setAllPhaseAdvices(data.all_advices);
    } else if (Array.isArray(data.all_advices_list)) {
      setAllPhaseAdvices(data.all_advices_list);
    } else if (Array.isArray(data.advices)) {
      setAllPhaseAdvices(data.advices);
    } else if (Array.isArray(data)) {
      setAllPhaseAdvices(data);
    } else {
      setAllPhaseAdvices([]);
    }
    lastAdvicesTreatmentRef.current = currentTreatmentId;
    // console.log('[selectedPatientProgram] Using advices from prop:', Array.isArray(data.all_advices) ? data.all_advices.length : 0, 'items');
    return;
  }

  // Fetch if treatment changed or prop not provided
  async function fetchAdvices() {
    if (!selectedPatient || !selectedPatient.user_id) return;
    try {
      const headers = {
        'Authorization': `Token ${token}`,
        'Accept': 'application/json'
      };
      const params = new URLSearchParams();
      if (currentTreatmentId) params.append('treatment_id', currentTreatmentId);
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const url = `${apiBaseUrl}/treatment/advices/patient/${selectedPatient.user_id}/${queryStr}`;
      // console.log('[selectedPatientProgram] Fetching advices:', url);
      
      const res = await fetch(url, { headers, method: 'GET' });
      if (!res.ok) throw new Error(`${res.status}`);
      
      const data = await res.json();
      if (Array.isArray(data.all_advices)) {
        setAllPhaseAdvices(data.all_advices);
      } else if (Array.isArray(data.all_advices_list)) {
        setAllPhaseAdvices(data.all_advices_list);
      } else if (Array.isArray(data.advices)) {
        setAllPhaseAdvices(data.advices);
      } else if (Array.isArray(data)) {
        setAllPhaseAdvices(data);
      } else {
        setAllPhaseAdvices([]);
      }
      lastAdvicesTreatmentRef.current = currentTreatmentId;
    } catch (err) {
      console.error('[selectedPatientProgram] Error fetching advices:', err);
      setAllPhaseAdvices([]);
    }
  }
  fetchAdvices();
}, [advices, props.treatmentId, selectedPatient, token]);

// Filter advices for selected phase
useEffect(() => {
  if (!selectedPhaseId || !allPhaseAdvices.length) {
    setAdvicesForSelectedPhase([]);
    setCurrentPhaseNameForAdvices('');
    setEditableAdvices([]);
    return;
  }
  
  const found = allPhaseAdvices.find(p => p.phase_id === selectedPhaseId);
  const advicesArr = Array.isArray(found?.advices) ? found.advices : [];
  
  // console.log('[selectedPatientProgram] Phase:', selectedPhaseId, 'Advices:', advicesArr.length);
  setAdvicesForSelectedPhase(advicesArr);
  setCurrentPhaseNameForAdvices(found?.phase_name || '');
  setEditableAdvices(advicesArr.map(a => ({ ...a, uniqueKey: a.id || uuidv4() })));
}, [selectedPhaseId, allPhaseAdvices]);

  // Add advice to local editable state, prevent duplicates by id
  const handleAddAdvice = (advice) => {
    if (advice.id && editableAdvices.some(a => a.id === advice.id)) return;
    const uniqueKey = advice.id ? advice.id : uuidv4();
    setEditableAdvices(prev => [
      ...prev,
      { ...advice, uniqueKey, index: prev.length + 1 }
    ]);
  };

  // Remove advice from local editable state, prevent error if not found
  const handleRemoveAdviceFrontend = (advice) => {
    setEditableAdvices(prev => {
      const filtered = prev.filter(a =>
        !(
          (advice.id && a.id === advice.id) ||
          (advice.uniqueKey && a.uniqueKey === advice.uniqueKey)
        )
      );
      console.log('[selectedPatientProgram] handleRemoveAdviceFrontend removed', advice.id || advice.uniqueKey, 'remaining:', filtered.length);
      return filtered;
    });
  };

  const saveAdvicesForPhase = async ({ phaseId, advices, phaseName, successMessage = 'All advice changes saved!' }) => {
    if (!selectedPatient || !phaseId) {
      return [];
    }

    try {
      const sanitizedAdvices = (advices || []).map((advice, index) => ({
        ...(advice.id && { id: advice.id }),
        identifier: advice.identifier || advice.id || advice.uniqueKey || uuidv4(),
        title: advice.title || '',
        advice: advice.advice || '',
        importance: advice.importance ?? (index + 1)
      }));

      const normalizedPhaseId = String(phaseId);
      const existingPhase = allPhaseAdvices.find((phase) => String(phase.phase_id) === normalizedPhaseId);
      const resolvedPhaseName = phaseName || existingPhase?.phase_name || '';

      const updateAdviceParams = new URLSearchParams();
      if (props.treatmentId) {
        updateAdviceParams.append('treatment_id', props.treatmentId);
      }
      const updateAdviceUrl = `${apiBaseUrl}/treatment/advices/patient/${selectedPatient.user_id}/?${updateAdviceParams.toString()}`;
      const response = await fetch(updateAdviceUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify({
          phase_id: normalizedPhaseId,
          advices: sanitizedAdvices
        })
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(`status ${response.status}: ${(data && data.error) || 'Failed to save advices'}`);

      const savedAdvices = (data && data.advices ? data.advices : sanitizedAdvices).map((advice, index) => ({
        ...advice,
        importance: advice.importance ?? (index + 1),
        uniqueKey: advice.id || advice.uniqueKey || uuidv4()
      }));

      setAllPhaseAdvices(prev => {
        const otherPhases = prev.filter((phase) => String(phase.phase_id) !== normalizedPhaseId);
        return [
          ...otherPhases,
          {
            phase_id: normalizedPhaseId,
            phase_name: resolvedPhaseName,
            advices: savedAdvices
          }
        ];
      });

      if (String(selectedPhaseId) === normalizedPhaseId) {
        setAdvicesForSelectedPhase(savedAdvices);
        setEditableAdvices(savedAdvices);
        setCurrentPhaseNameForAdvices(resolvedPhaseName);
      }

      setSnackbarMsg(successMessage);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      if (typeof fetchPatientAllData === 'function') {
        const diagId = Array.isArray(diagnosis) ? (diagnosis[0]?.id || diagnosis[0]?.diagnosis?.id) : (diagnosis?.id || diagnosis?.diagnosis?.id);
        try {
          await fetchPatientAllData(selectedPatient.user_id, diagId || null, props.treatmentId || null);
        } catch (err) {
          console.warn('Advice save: parent refresh failed', err);
        }
      }
      try {
        window.dispatchEvent(new CustomEvent('advicesRefreshed', { detail: { userId: selectedPatient.user_id, phaseId: normalizedPhaseId } }));
      } catch (e) {}

      return savedAdvices;
    } catch (error) {
      console.error('Error saving advices', error);
      setSnackbarMsg('Error saving advices: ' + error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw error;
    }
  };

  const handleSaveAllAdvices = async () => {
    if (!selectedPatient || !selectedPhaseId) return;
    await saveAdvicesForPhase({
      phaseId: selectedPhaseId,
      advices: editableAdvices,
      phaseName: currentPhaseNameForAdvices,
      successMessage: 'All advice changes saved!',
    });
  };

  // Snackbar close handler
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Report dialog handlers
  const handleOpenReportDialog = (mode = 'create', report = null) => {
    setReportMode(mode);
    setSelectedReport(report);
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setSelectedReport(null);
  };

  const handleSaveReport = async (reportData) => {
    try {
      // Save to backend using the appropriate endpoint
      const result = await saveReport(reportData, reportData.reportType, token);
      
      console.log('Report saved successfully:', result);
      setSnackbarMsg(`${reportData.reportType === 'epicrisis' ? 'Epicrisis' : 'Status report'} saved successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseReportDialog();
    } catch (error) {
      console.error('Error saving report:', error);
      setSnackbarMsg(error.message || 'Error saving report');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

    // Update advice importance in editableAdvices
  const handleUpdateAdviceImportance = (advice, newImportance) => {
    setEditableAdvices(prev =>
      prev.map(a =>
        (a.id && a.id === advice.id) || (a.uniqueKey && a.uniqueKey === advice.uniqueKey)
          ? { ...a, importance: newImportance }
          : a
      )
    );
  };
  
  const simpleFormatter = (text, len) => {
    if (!text) return '';
    var i = 0;
    for(i = 0; i < text.length; i = i+len){
      if (i !== 0) {
        const subString = text.slice(0, i).split("").reverse().join(""); // abc d -> d cba
        const splitIndex = subString.length - subString.indexOf(' ');
        text = text.slice(0, splitIndex) + "\n" + text.slice(splitIndex);
      }
    }
    return text;
  }

  const sortedExercises = React.useMemo(() => {
    return [...exercisesState].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  }, [exercisesState]);

  // Drag-and-hold reorder state. Indices refer to positions within
  // the sorted (by `index`) list rendered to screen, so the drop
  // target lines up with what the user sees.
  const [dragIndex, setDragIndex] = React.useState(null);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);

  // Move an exercise from one position to another (1-based positions
  // are mapped via the sorted list). Renumbers `index` 1..N so the
  // change persists when saved by the parent.
  const reorderExerciseByPosition = React.useCallback((fromPos, toPos) => {
    if (fromPos === toPos) return;
    setExercisesState(prev => {
      const sorted = [...prev].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      const len = sorted.length;
      if (len < 2) return prev;
      const from = Math.max(0, Math.min(len - 1, fromPos));
      const to = Math.max(0, Math.min(len - 1, toPos));
      if (from === to) return prev;
      const next = sorted.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      const reindexed = next.map((ex, i) => ({ ...ex, index: i + 1 }));
      // Preserve any items that weren't part of the sorted view (shouldn't
      // happen — sorted is built from prev — but defensive).
      const byKey = new Map(reindexed.map(ex => [ex.uniqueKey ?? ex.id, ex]));
      return prev.map(ex => byKey.get(ex.uniqueKey ?? ex.id) || ex);
    });
  }, []);

  const FrequencyType = [
    {
      value: "daily",
      label: "Daily"
    },
    {
      value: "weekly",
      label: "Weekly"
    },
  ];


  return (
    <React.Fragment>
      <CssBaseline />
      <main>
        <Container className={classes.cardGrid} maxWidth={false} style={{paddingLeft: 0, paddingRight: 0, marginTop: 0}}>
          <Grid container spacing={0} direction="row" style={{width: '100%', margin: 0}}>
            <Grid item sm={12} lg={12} style={{paddingLeft: 0, paddingRight: 0, paddingTop: 0}}>
                {/* Floating tab bar */}
                <Paper className={classes.tabsRoot} elevation={0}>
                  <Tabs
                    value={
                      activeTab === 'clinicCalendar' ? 0
                      : activeTab === 'Overview' ? 1
                      : activeTab === 'journal' ? 2
                      : activeTab === 'exerciseProgram' ? 3
                      : activeTab === 'patientDetails' && patientDetailsTab === 'payment' ? 4
                      : activeTab === 'patientDetails' && patientDetailsTab === 'insurance' ? 5
                      : activeTab === 'patientDetails' && patientDetailsTab === 'personalInformation' ? 6
                      : 0
                    }
                    onChange={(e, val) => {
                      if (val === 0) runWithJournalUnsavedGuard('opening the calendar tab', () => setActiveTab('clinicCalendar'));
                      else if (val === 1) runWithJournalUnsavedGuard('switching to the overview tab', () => setActiveTab('Overview'));
                      else if (val === 2) runWithJournalUnsavedGuard('opening the journal tab', () => setActiveTab('journal'));
                      else if (val === 3) runWithJournalUnsavedGuard('opening the program tab', () => handleProgramTabClick());
                      else if (val === 4) runWithJournalUnsavedGuard('opening payment details', () => openPatientDetailsTab('payment'));
                      else if (val === 5) runWithJournalUnsavedGuard('opening insurance details', () => openPatientDetailsTab('insurance'));
                      else if (val === 6) runWithJournalUnsavedGuard('opening patient details', () => openPatientDetailsTab('personalInformation'));
                    }}
                    classes={{ root: classes.tabsControlRoot, indicator: classes.tabIndicator }}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Calendar" className={classes.tabRoot} />
                    <Tab label="Overview" className={classes.tabRoot} />
                    <Tab label="Journal" className={classes.tabRoot} />
                    <Tab label="Program" className={classes.tabRoot} disabled={!canAdaptiveExercise} />
                    <Tab label="Payment" className={classes.tabRoot} />
                    <Tab label="Insurance" className={classes.tabRoot} />
                    <Tab label="Patient" className={classes.tabRoot} />
                  </Tabs>
                </Paper>
                {/* Tab content */}
                {/* Tab content for left panel */}
                {activeTab === 'exerciseProgram' && canAdaptiveExercise && (
                  <>
                    {!showAdviceInExerciseTab ? (
                      <Grid container spacing={1}>
                        {/* Selected Patient Exercises - 5/12 */}
                        <Grid item xs={12} md={6} lg={5} xl={4}>
                          <Card style={{ background: '#f7faff', borderRadius: 16, boxShadow: '0 6px 32px rgba(0,45,85,0.18)', overflow: 'hidden' }}>
                            <div className={classes.panelHeader}>
                              <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', position: 'absolute', left: 16, paddingLeft: 5 }}>
                                Exercises
                              </Typography>
                              <Box display="flex" alignItems="center" justifyContent="center" style={{ flex: 1, gap: 6, paddingLeft: "2.5%" }}>
                                {!selectedPhaseId ? (
                                  <Typography variant="subtitle1" style={{ color: "#FF7F27", fontStyle: "italic", fontSize: 'clamp(0.69rem, 1.04vw, 0.93rem)' }}>
                                    Select a patient to view program
                                  </Typography>
                                ) : (
                                  <Select disableUnderline displayEmpty
                                    value={selectedPhaseId}
                                    onChange={e => { handlePhaseDropdownChange(e); }}
                                    className={classes.orangeSelect}
                                    style={{ maxWidth: 215 }}
                                    MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}
                                  >
                                    {exercisePhases.map(option => (
                                      <MenuItem key={option.value} value={option.value} className={classes.menuItem}
                                        style={{ fontWeight: selectedPhaseId === option.value ? 700 : 500 }}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                                <Select disableUnderline
                                  value={showAdviceInExerciseTab ? "advice" : "exercises"}
                                  onChange={e => setShowAdviceInExerciseTab(e.target.value === "advice")}
                                  className={classes.orangeSelect}
                                  MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8  } } }}
                                >
                                  <MenuItem value="exercises" className={classes.menuItem}>Exercises</MenuItem>
                                  <MenuItem value="advice" className={classes.menuItem}>Advice</MenuItem>
                                </Select>                                
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5} style={{ position: 'absolute', right: 16 }}>
                                <IconButton
                                  size="small"
                                  onClick={showAdviceInExerciseTab ? handleSaveAllAdvices : handleSaveAllExercises}
                                  disabled={!showAdviceInExerciseTab && activeSessionLocked}
                                  className={`${classes.iconButton} ${classes.panelIconButton}`}
                                  title={showAdviceInExerciseTab ? 'Save all advices' : 'Save all exercises'}
                                >
                                  <SaveIcon />
                                </IconButton>
                                {typeof fetchExercisesForPhase === 'function' && (
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      // Full refresh: pull latest phases/advices/exercises from parent FIRST
                                      if (typeof fetchPatientAllData === 'function' && selectedPatient && selectedPatient.user_id) {
                                        const diagId = Array.isArray(diagnosis) ? (diagnosis[0]?.diagnosis_id || diagnosis[0]?.id) : (diagnosis?.diagnosis_id || diagnosis?.id);
                                        const treatmentId = selectedPatient.treatment_id || null;
                                        console.log('[Refresh Button] Refreshing all patient data with treatmentId:', treatmentId);
                                        try {
                                          // Use treatment_id for proper scoping (treatment_id is more precise than diagnosis_id)
                                          await fetchPatientAllData(selectedPatient.user_id, diagId || null, treatmentId);
                                          // After refreshing data, re-fetch exercises for current phase
                                          // selectedPhaseId should still be valid if phase exists in refreshed data
                                          if (selectedPhaseId && typeof fetchExercisesForPhase === 'function') {
                                            await fetchExercisesForPhase(selectedPhaseId);
                                          }
                                        } catch (err) {
                                          console.warn('Refresh (fetchPatientAllData) failed', err);
                                        }
                                      } else {
                                        // Fallback: just refresh exercises if fetchPatientAllData not available
                                        await fetchExercisesForPhase(selectedPhaseId);
                                      }
                                      if (selectedPatient && selectedPatient.user_id) {
                                        const status = await fetchActiveSessionStatus(selectedPatient.user_id, token);
                                        if (status && status.active) {
                                          setActiveSessionInfo(status);
                                          setActiveSessionLocked(true);
                                        } else {
                                          setActiveSessionInfo(null);
                                          setActiveSessionLocked(false);
                                        }
                                      }
                                    }}
                                    className={`${classes.iconButton} ${classes.panelIconButton}`}
                                    style={{ marginLeft: 4 }}
                                    title="Refresh exercises"
                                  >
                                    <RefreshIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </div>
                            {/*
                              REGULATORY COMPLIANCE ANCHOR: CLASS I / NON-MDR SAFEGUARD
                              This pending-suggestion banner is retained as a dormant legacy UI shell.
                              Current backend and mode sanitization prevent live clinical CDS rendering here.
                            */}
                            {/* Pending exercise suggestions banner */}
                            {pendingSuggestions.length > 0 && (
                              <Box style={{ padding: '8px 12px', background: '#fff8e1', borderBottom: '1px solid #ffe082', borderRadius: 0 }}>
                                <Typography style={{ fontSize: 13, fontWeight: 600, color: '#e65100', marginBottom: 4 }}>
                                  {pendingSuggestions.length} pending exercise suggestion{pendingSuggestions.length > 1 ? 's' : ''}
                                </Typography>
                                {pendingSuggestions.map(s => (
                                  <Box key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', flexWrap: 'wrap' }}>
                                    <Typography style={{ fontSize: 12, flex: 1 }}>
                                      <strong>{s.original_title || s.original_identifier}</strong> → <strong>{s.replacement_title || s.replacement_identifier}</strong>
                                      <span style={{ color: '#888', marginLeft: 6 }}>({s.reason_display || s.reason})</span>
                                    </Typography>
                                    <Box style={{ display: 'flex', gap: 4 }}>
                                      <button
                                        onClick={() => handleAcceptSuggestion(s.id)}
                                        style={{ fontSize: 11, padding: '2px 10px', borderRadius: 8, border: '1px solid #43a047', background: '#e8f5e9', color: '#2e7d32', cursor: 'pointer', fontWeight: 600 }}
                                      >Accept</button>
                                      <button
                                        onClick={() => handleRejectSuggestion(s.id)}
                                        style={{ fontSize: 11, padding: '2px 10px', borderRadius: 8, border: '1px solid #e53935', background: '#ffebee', color: '#c62828', cursor: 'pointer', fontWeight: 600 }}
                                      >Reject</button>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {/* Bulk-edit toolbar */}
                            {sortedExercises.length > 0 && (
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', marginBottom: -7.5, background: 'transparent', borderBottom: '0px solid #d0e4f7', flexWrap: 'wrap' }}>
                                <Box display="flex" alignItems="center" gap={1} style={{ opacity: activeSessionLocked ? 0.5 : 1, pointerEvents: activeSessionLocked ? 'none' : 'auto', paddingLeft: 9, flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={handleOpenPhaseProgressionDialog}
                                    disabled={phaseProgressionLoading || !phaseProgressionData}
                                    className={classes.cancelButton}
                                    style={{ minWidth: 'auto', padding: '2px 10px' }}
                                  >
                                    Settings
                                  </Button>
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5} style={{ opacity: activeSessionLocked ? 0.5 : 1, pointerEvents: activeSessionLocked ? 'none' : 'auto', marginLeft: 'auto', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                  <Typography style={{ fontSize: 'clamp(0.66rem, 0.88vw, 0.82rem)', color: '#002D55', fontWeight: 600, whiteSpace: 'nowrap', marginRight: 4, paddingLeft: 9 }}>
                                    Apply to all:
                                  </Typography>
                                  <Select
                                    disableUnderline
                                    value={bulkEditParam}
                                    onChange={e => { setBulkEditParam(e.target.value); setBulkEditValue(bulkEditParamDefaults[e.target.value]); }}
                                    className={classes.orangeSelect}
                                    style={{ minWidth: 70, marginRight: 4 }}
                                    MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8  } } }}
                                  >
                                    <MenuItem value="sets" className={classes.menuItem}>Sets</MenuItem>
                                    <MenuItem value="repetitions" className={classes.menuItem}>Rep(s)</MenuItem>
                                    <MenuItem value="repetitionMaximum" className={classes.menuItem}>RM</MenuItem>
                                    <MenuItem value="weightKg" className={classes.menuItem}>Weight (Kg)</MenuItem>
                                    <MenuItem value="weightTotalKg" className={classes.menuItem}>Weight (Total Kg)</MenuItem>
                                    <MenuItem value="weightLbs" className={classes.menuItem}>Weight (Lbs)</MenuItem>
                                    <MenuItem value="weightTotalLbs" className={classes.menuItem}>Weight (Total Lbs)</MenuItem>
                                    <MenuItem value="time" className={classes.menuItem}>Time (s)</MenuItem>
                                    <MenuItem value="doseType" className={classes.menuItem}>Dose Type</MenuItem>
                                    <MenuItem value="pause" className={classes.menuItem}>Pause (s)</MenuItem>
                                    <MenuItem value="frequency" className={classes.menuItem}>Freq.</MenuItem>
                                    <MenuItem value="frequencyType" className={classes.menuItem}>Schedule</MenuItem>
                                  </Select>
                                  {['sets', 'repetitions', 'repetitionMaximum', 'weightKg', 'weightTotalKg', 'weightLbs', 'weightTotalLbs', 'time', 'pause', 'frequency'].includes(bulkEditParam) ? (
                                    <input
                                      type="number"
                                      min={0}
                                      value={bulkEditValue}
                                      onChange={e => setBulkEditValue(e.target.value)}
                                      style={{
                                        width: 'clamp(55px, 3.75vw, 72px)',
                                        background: '#fff',
                                        color: '#002D55',
                                        fontWeight: 500,
                                        fontSize: 'clamp(0.66rem, 0.88vw, 0.82rem)',
                                        border: '1px solid #ccc',
                                        borderRadius: 14,
                                        padding: '3.5px 10px',
                                        outline: 'none',
                                        cursor: 'text',
                                        fontFamily: 'inherit',
                                        transition: 'border-color 0.2s',
                                        marginRight: 6
                                      }}
                                      onFocus={e => e.target.style.borderColor = '#FF7F27'}
                                      onBlur={e => e.target.style.borderColor = '#ccc'}
                                    />
                                  ) : bulkEditParam === 'doseType' ? (
                                    <Select
                                      disableUnderline
                                      value={bulkEditValue}
                                      onChange={e => setBulkEditValue(e.target.value)}
                                      className={classes.orangeSelect}
                                      style={{ minWidth: 80 }}
                                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8  } } }}
                                    >
                                      <MenuItem value="time" className={classes.menuItem}>Time</MenuItem>
                                      <MenuItem value="repetitions" className={classes.menuItem}>Repetitions</MenuItem>
                                      <MenuItem value="repetition_maximum" className={classes.menuItem}>Repetition Maximum (RM)</MenuItem>
                                      <MenuItem value="weight_kg" className={classes.menuItem}>Weight (Kg)</MenuItem>
                                      <MenuItem value="weight_total_kg" className={classes.menuItem}>Weight (Total Kg)</MenuItem>
                                      <MenuItem value="weight_lbs" className={classes.menuItem}>Weight (Lbs)</MenuItem>
                                      <MenuItem value="weight_total_lbs" className={classes.menuItem}>Weight (Total Lbs)</MenuItem>
                                    </Select>
                                  ) : (
                                    <Select
                                      disableUnderline
                                      value={bulkEditValue}
                                      onChange={e => setBulkEditValue(e.target.value)}
                                      className={classes.orangeSelect}
                                      style={{ minWidth: 90 }}
                                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8  } } }}
                                    >
                                      <MenuItem value="daily" className={classes.menuItem}>Daily</MenuItem>
                                      <MenuItem value="weekly" className={classes.menuItem}>Weekly</MenuItem>
                                    </Select>
                                  )}
                                  <Tooltip title="Apply to all" arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={activeSessionLocked}
                                        onClick={() => {
                                          setExercisesState(prev => prev.map(ex => ({
                                            ...ex,
                                            ...(bulkEditParam === 'doseType'
                                              ? normalizeEditedExerciseParams({ ...ex, doseType: bulkEditValue })
                                              : {
                                                [bulkEditParam]: ['sets', 'repetitions', 'repetitionMaximum', 'time', 'pause', 'frequency'].includes(bulkEditParam)
                                                  ? parseInt(bulkEditValue, 10)
                                                  : ['weightKg', 'weightTotalKg', 'weightLbs', 'weightTotalLbs'].includes(bulkEditParam)
                                                    ? parseFloat(bulkEditValue) || 0
                                                    : bulkEditValue,
                                              }),
                                          })));
                                        }}
                                        style={{ padding: 2 }}
                                      >
                                        <CheckCircleIcon style={{ color: activeSessionLocked ? '#aaa' : '#0fbb51', fontSize: 'clamp(18px, 1.6vw, 24px)' }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Cancel" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setBulkEditParam('sets');
                                        setBulkEditValue(bulkEditParamDefaults['sets']);
                                      }}
                                      style={{ padding: 2 }}
                                    >
                                      <CancelIcon style={{ color: '#e53935', fontSize: 'clamp(18px, 1.6vw, 24px)' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            )}
                            <Box style={{ padding: 16 }}>
                              
                              {activeSessionInfo && (
                                <Box mt={1}>
                                  <Alert severity="warning">
                                    Patient has an active session in {activeSessionInfo.phase || 'current phase'}. Editing is locked until the session ends. Click refresh button to re-check status.
                                  </Alert>
                                </Box>
                              )}
                              
                            {<Box position="relative">
                              {activeSessionLocked && (
                                <Box
                                  position="absolute"
                                  top={0}
                                  left={0}
                                  right={0}
                                  bottom={120}
                                  zIndex={10}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' }}
                                >
                                  <Alert severity="warning" style={{ maxWidth: 520 }}>
                                    Patient has an active session. Editing is temporarily locked. Please retry after the session ends. Click refresh button to re-check status.
                                  </Alert>
                                </Box>
                              )}
                              <Grid container xs={12} direction="row">
                                {selectedPatient == undefined || isLoading
                                  ? skeletonArray.map((_, idx) => (
                                      <Grid item key={idx} xs={12}>
                                        <Paper elevation={2} style={{ marginBottom: 8 }}>
                                          <Card
                                            className={classes.cardOriginal}
                                            style={{
                                              display: "flex",
                                              flexDirection: "row",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            {/* Picture skeleton */}
                                           <div style={{ width: 130, minWidth: 130, marginRight: 12, marginLeft: 16, marginTop: 13, display: "flex", flexDirection: "column", alignItems: "flex-start", height: "100%", justifyContent: "flex-start" }}>
                                              {/* Index skeleton above image skeleton */}
                                              <Skeleton variant="rect" width={50} height={20} style={{ marginBottom: 16, marginTop: 4 }} />
                                              <Skeleton variant="rect" width={132} height={92} />
                                            </div>
                                            {/* Right content */}
                                            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", height: "100%", marginTop: 8 }}>
                                              {/* Title skeleton */}
                                              <Skeleton variant="text" width="60%" height={32} style={{ marginBottom: 12 }} />
                                              {/* Two rows of textfield skeletons */}
                                              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                              </div>
                                              <div style={{ display: "flex", gap: 8 }}>
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                                <Skeleton variant="rect" width="20%" height={32} style={{ borderRadius: 4 }} />
                                              </div>
                                            </div>
                                          </Card>
                                        </Paper>
                                      </Grid>
                                    ))
                                  : exercisePhases.some(opt => opt.value === selectedPhaseId)
                                    ? sortedExercises
                                      .map((exercise, listIndex) => (
                                        <Grid item key={exercise.id ? exercise.id : exercise.uniqueKey} xs={12}>
                                              
                                          <Paper
                                            elevation={2}
                                            draggable={!activeSessionLocked}
                                            onDragStart={(e) => {
                                              if (activeSessionLocked) return;
                                              setDragIndex(listIndex);
                                              try { e.dataTransfer.effectAllowed = 'move'; } catch (_) {}
                                            }}
                                            onDragOver={(e) => {
                                              if (activeSessionLocked || dragIndex === null) return;
                                              e.preventDefault();
                                              try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
                                              if (dragOverIndex !== listIndex) setDragOverIndex(listIndex);
                                            }}
                                            onDragLeave={() => {
                                              if (dragOverIndex === listIndex) setDragOverIndex(null);
                                            }}
                                            onDrop={(e) => {
                                              if (activeSessionLocked || dragIndex === null) return;
                                              e.preventDefault();
                                              reorderExerciseByPosition(dragIndex, listIndex);
                                              setDragIndex(null);
                                              setDragOverIndex(null);
                                            }}
                                            onDragEnd={() => {
                                              setDragIndex(null);
                                              setDragOverIndex(null);
                                            }}
                                            style={{
                                              marginBottom: 'clamp(5px, 0.43vw, 8px)',
                                              padding: 'clamp(5px, 0.43vw, 8px)',
                                              background: '#eaf2fb',
                                              borderRadius: 14,
                                              border: dragOverIndex === listIndex && dragIndex !== null && dragIndex !== listIndex
                                                ? '2px dashed #FF7F27'
                                                : '1px solid #d0e4f7',
                                              opacity: dragIndex === listIndex ? 0.5 : 1,
                                              cursor: activeSessionLocked ? 'default' : 'grab',
                                            }}
                                          >
                                            <Card className={`${classes.cardOriginal} ${classes.cardClickable}`} style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
                                              {/* Header: Index + Title + Buttons */}
                                              <Grid container spacing={1} alignItems="center" style={{ padding: 'clamp(5px, 0.43vw, 8px) clamp(5px, 0.43vw, 8px) 0 clamp(5px, 0.43vw, 8px)', marginBottom: 0, flexWrap: 'nowrap' }}>
                                                {/* Index */}
                                                <Grid item style={{ flexShrink: 0 }}>
                                                  <TextField
                                                    type="number"
                                                    InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                    value={exercise.index}
                                                    onChange={e => {
                                                      const newIndex = parseInt(e.target.value, 10);
                                                      setExercisesState(prev =>
                                                        prev.map(ex =>
                                                          (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                            ? { ...ex, index: newIndex }
                                                            : ex
                                                        )
                                                      );
                                                    }}
                                                    size="small"
                                                    style={{ width: 'clamp(22px, 1.85vw, 35px)', backgroundColor: 'transparent', borderRadius: 0, borderBottom: '2px solid #FF7F27' }}
                                                    InputProps={{ style: { color: '#002D55', fontSize: 'clamp(0.69rem, 1.04vw, 0.93rem)' } }}
                                                    classes={{ root: `${classes.orangeFocused} ${classes.alwaysOrangeUnderline} ${classes.alwaysShowSpinButtons}` }}
                                                  />
                                                </Grid>
                                                {/* Title */}
                                                <Grid item style={{ flex: 1, minWidth: 0 }}>
                                                  <Typography className={classes.exerciseTypographyCardStyling} style={{ textAlign: 'left', fontWeight: 600, margin: 0, wordBreak: 'break-word', fontSize: 'clamp(0.68rem, calc(2px + 0.722vw), 1.0rem)', opacity: exercise.is_paused ? 0.55 : 1 }}>
                                                    {simpleFormatter(exercise.title, 56)}
                                                  </Typography>
                                                  {exercise.is_paused && (
                                                    <Typography style={{ color: '#8a5b00', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', fontWeight: 700, marginTop: 2 }}>
                                                      Paused
                                                    </Typography>
                                                  )}
                                                </Grid>
                                                <Grid item style={{ flexShrink: 0 }}>
                                                  <Tooltip title={exercise.is_paused ? 'Resume exercise' : 'Pause exercise'}>
                                                    <IconButton
                                                      size="small"
                                                      className={classes.removeButton}
                                                      style={{ width: 'clamp(22px, 1.65vw, 32px)', height: 'clamp(22px, 1.65vw, 32px)', padding: 0, borderRadius: 14 }}
                                                      onClick={() => setExercisesState(prev => prev.map(ex => (
                                                        (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                          ? { ...ex, is_paused: !Boolean(ex.is_paused) }
                                                          : ex
                                                      )))}
                                                      disabled={activeSessionLocked}
                                                    >
                                                      {exercise.is_paused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                                                    </IconButton>
                                                  </Tooltip>
                                                </Grid>
                                                {/* Edit Button */}
                                                <Grid item style={{ flexShrink: 0 }}>
                                                  <Button
                                                    size="small"
                                                    className={classes.removeButton}
                                                    style={{ minWidth: 0, width: 'clamp(22px, 1.65vw, 32px)', height: 'clamp(22px, 1.65vw, 32px)', padding: 0, borderRadius: 14, fontSize: 'clamp(0.69rem, 0.85vw, 0.93rem)' }}
                                                    onClick={() => handleOpenEditExerciseDialog(exercise)}
                                                    title="Edit exercise"
                                                  >
                                                    ✎
                                                  </Button>
                                                </Grid>
                                                {/* Remove Button */}
                                                <Grid item style={{ flexShrink: 0 }}>
                                                  <Button
                                                    size="small"
                                                    className={classes.removeButton}
                                                    style={{ minWidth: 0, width: 'clamp(22px, 1.65vw, 32px)', height: 'clamp(22px, 1.65vw, 32px)', padding: 0, borderRadius: 14, fontSize: 'clamp(0.69rem, 0.85vw, 0.93rem)' }}
                                                    onClick={() => handleRemoveExercise(exercise, 'remove')}
                                                    title="Remove exercise"
                                                  >
                                                    ✕
                                                  </Button>
                                                </Grid>
                                              </Grid>

                                              {/* Body: Image + Parameters */}
                                              <Grid container spacing={1} alignItems="stretch" style={{ padding: 'clamp(6px, 0.63vw, 11px)' }}>
                                                {/* Image Column */}
                                                <Grid item xs={4} sm={3} md={3} style={{ display: 'flex', flexDirection: 'column' }}>
                                                  <div style={{ width: '100%', height: '100%', minHeight: '100%', display: 'flex' }}>
                                                    <ExerciseImage
                                                      exercise={exercise}
                                                      style={{ width: '100%', maxWidth: 100, maxHeight: 78, height: '100%', borderRadius: 8, objectFit: 'cover', display: 'block', flex: 1 }}
                                                      skeletonStyle={{ width: '100%', height: '100%', maxHeight: 100, borderRadius: 8, display: 'block', flex: 1 }}
                                                    />
                                                  </div>
                                                </Grid>

                                                {/* Parameters Column */}
                                                <Grid item xs={8} sm={9} md={9} style={{ display: 'flex' }}>
                                                  <div className={classes.exCardParams} style={{ width: '100%', height: '100%' }}>
                                                  <Grid container spacing={1}>
                                                    {/* Row 1: Sets, Time, Pause */}
                                                    <Grid item xs={6} sm={4} md={4}>
                                                      <TextField
                                                        label="Sets"
                                                        type="number"
                                                        InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                        value={exercise.sets}
                                                        onChange={e => {
                                                          const newSets = parseInt(e.target.value, 10);
                                                          setExercisesState(prev =>
                                                            prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? { ...ex, sets: newSets }
                                                                : ex
                                                            )
                                                          );
                                                        }}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                        InputProps={{ style: { color: '#002D55' } }}
                                                        classes={{ root: classes.orangeFocused }}
                                                      />
                                                    </Grid>
                                                    {exercise.doseType === "time" ? (
                                                      <Grid item xs={6} sm={4} md={4}>
                                                        <TextField
                                                          label="Time (s)"
                                                          type="number"
                                                          InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                          value={parseSeconds(exercise.time)}
                                                          onChange={e => {
                                                            const newTime = parseInt(e.target.value, 10);
                                                            setExercisesState(prev =>
                                                              prev.map(ex =>
                                                                (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                  ? { ...ex, time: newTime }
                                                                  : ex
                                                              )
                                                            );
                                                          }}
                                                          variant="outlined"
                                                          size="small"
                                                          fullWidth
                                                          style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                          InputProps={{ style: { color: '#002D55' } }}
                                                          classes={{ root: classes.orangeFocused }}
                                                        />
                                                      </Grid>
                                                    ) : exercise.doseType === "repetition_maximum" ? (
                                                      <Grid item xs={6} sm={4} md={4}>
                                                        <TextField
                                                          label="Repetition Maximum (RM)"
                                                          type="number"
                                                          inputProps={{ min: 1 }}
                                                          InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                          value={exercise.repetitionMaximum ?? 1}
                                                          onChange={e => {
                                                            const repetitionMaximum = parseInt(e.target.value, 10);
                                                            setExercisesState(prev => prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? { ...ex, repetitionMaximum }
                                                                : ex
                                                            ));
                                                          }}
                                                          variant="outlined"
                                                          size="small"
                                                          fullWidth
                                                          style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                          InputProps={{ style: { color: '#002D55' } }}
                                                          classes={{ root: classes.orangeFocused }}
                                                        />
                                                      </Grid>
                                                    ) : WEIGHT_DOSE_OPTIONS[exercise.doseType] ? (
                                                      <Grid item xs={6} sm={4} md={4}>
                                                        <TextField
                                                          label={(
                                                            <Tooltip title={WEIGHT_DOSE_OPTIONS[exercise.doseType].tooltip} arrow>
                                                              <span>{WEIGHT_DOSE_OPTIONS[exercise.doseType].label}</span>
                                                            </Tooltip>
                                                          )}
                                                          type="number"
                                                          inputProps={{ min: 0, step: 0.01 }}
                                                          InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                          value={exercise[WEIGHT_DOSE_OPTIONS[exercise.doseType].field] ?? 0}
                                                          onFocus={e => {
                                                            if (Number(e.target.value) === 0) e.target.select();
                                                          }}
                                                          onChange={e => {
                                                            const field = WEIGHT_DOSE_OPTIONS[exercise.doseType].field;
                                                            const value = e.target.value;
                                                            setExercisesState(prev => prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? { ...ex, [field]: value }
                                                                : ex
                                                            ));
                                                          }}
                                                          onBlur={e => {
                                                            const field = WEIGHT_DOSE_OPTIONS[exercise.doseType].field;
                                                            const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                            setExercisesState(prev => prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? { ...ex, [field]: value }
                                                                : ex
                                                            ));
                                                          }}
                                                          variant="outlined"
                                                          size="small"
                                                          fullWidth
                                                          style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                          InputProps={{ style: { color: '#002D55' } }}
                                                          classes={{ root: classes.orangeFocused }}
                                                        />
                                                      </Grid>
                                                    ) : (
                                                      <Grid item xs={6} sm={4} md={4}>
                                                        <TextField
                                                          label="Rep(s)"
                                                          type="number"
                                                          InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                          value={exercise.repetitions}
                                                          onChange={e => {
                                                            const newReps = parseInt(e.target.value, 10);
                                                            setExercisesState(prev =>
                                                              prev.map(ex =>
                                                                (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                  ? { ...ex, repetitions: newReps }
                                                                  : ex
                                                              )
                                                            );
                                                          }}
                                                          variant="outlined"
                                                          size="small"
                                                          fullWidth
                                                          style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                          InputProps={{ style: { color: '#002D55' } }}
                                                          classes={{ root: classes.orangeFocused }}
                                                        />
                                                      </Grid>
                                                    )}
                                                    <Grid item xs={6} sm={4} md={4}>
                                                      <TextField
                                                        select
                                                        label="Dose Type"
                                                        value={exercise.doseType || "time"}
                                                        onChange={(e) => {
                                                          const newDoseType = e.target.value;
                                                          setExercisesState(prev =>
                                                            prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) ||
                                                              (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? normalizeEditedExerciseParams({ ...ex, doseType: newDoseType })
                                                                : ex
                                                            )
                                                          );
                                                        }}
                                                        SelectProps={{ MenuProps: PHASE_PROGRESSION_SELECT_MENU_PROPS }}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                        style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                        InputProps={{ style: { color: '#002D55' } }}
                                                        classes={{ root: classes.orangeFocused }}
                                                      >
                                                        <MenuItem value="time" className={classes.menuItem}>Time</MenuItem>
                                                        <MenuItem value="repetitions" className={classes.menuItem}>Repetitions</MenuItem>
                                                        <MenuItem value="repetition_maximum" className={classes.menuItem}>Repetition Maximum (RM)</MenuItem>
                                                        <MenuItem value="weight_kg" className={classes.menuItem}>Weight (Kg)</MenuItem>
                                                        <MenuItem value="weight_total_kg" className={classes.menuItem}>Weight (Total Kg)</MenuItem>
                                                        <MenuItem value="weight_lbs" className={classes.menuItem}>Weight (Lbs)</MenuItem>
                                                        <MenuItem value="weight_total_lbs" className={classes.menuItem}>Weight (Total Lbs)</MenuItem>
                                                      </TextField>
                                                    </Grid>
                                                    
                                                    <Grid item xs={6} sm={4} md={4}>
                                                      <TextField
                                                        label="Pause (s)"
                                                        type="number"
                                                        InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                        value={parseSeconds(exercise.pause)}
                                                        onChange={e => {
                                                          const newPause = parseInt(e.target.value, 10);
                                                          setExercisesState(prev =>
                                                            prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? { ...ex, pause: newPause }
                                                                : ex
                                                            )
                                                          );
                                                        }}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                        InputProps={{ style: { color: '#002D55' } }}
                                                        classes={{ root: classes.orangeFocused }}
                                                      />
                                                    </Grid>

                                                    {/* Row 2: Frequency, Schedule */}
                                                    <Grid item xs={6} sm={4} md={4}>
                                                      <TextField
                                                        label="Freq."
                                                        type="number"
                                                        InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                        value={exercise.frequency}
                                                        onChange={e => {
                                                          const newFrequency = parseInt(e.target.value, 10);
                                                          setExercisesState(prev =>
                                                            prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) || (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? { ...ex, frequency: newFrequency }
                                                                : ex
                                                            )
                                                          );
                                                        }}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                        InputProps={{ style: { color: '#002D55' } }}
                                                        classes={{ root: classes.orangeFocused }}
                                                      />
                                                    </Grid>
                                                    <Grid item xs={6} sm={4} md={4}>
                                                      <TextField
                                                        select
                                                        label="Schedule"
                                                        value={exercise.frequencyType || "daily"}
                                                        onChange={(e) => {
                                                          const newFreqType = e.target.value;
                                                          setExercisesState(prev =>
                                                            prev.map(ex =>
                                                              (exercise.id && ex.id === exercise.id) ||
                                                              (!exercise.id && ex.uniqueKey === exercise.uniqueKey)
                                                                ? { ...ex, frequencyType: newFreqType }
                                                                : ex
                                                            )
                                                          );
                                                        }}
                                                        SelectProps={{ MenuProps: PHASE_PROGRESSION_SELECT_MENU_PROPS }}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                                                        style={{ backgroundColor: '#F0F8FF', color: '#002D55', borderRadius: 14 }}
                                                        InputProps={{ style: { color: '#002D55' } }}
                                                        classes={{ root: classes.orangeFocused }}
                                                      >
                                                        {FrequencyType.map((option) => (
                                                          <MenuItem key={option.value} value={option.value} className={classes.menuItem}>
                                                            {option.label}
                                                          </MenuItem>
                                                        ))}
                                                      </TextField>
                                                    </Grid>
                                                  </Grid>
                                                  </div>
                                                </Grid>
                                              </Grid>
                                            </Card>
                                          </Paper>
                                        </Grid>
                                      )): null}
                              </Grid>
                              <Divider style={{ marginTop: 12, marginBottom: 12 }} />
                              <Box mt={1} mb={2}>
                                <Button
                                  variant="contained"
                                  style={{ backgroundColor: '#FF7F27', color: 'white' }}
                                  size="large"
                                  onClick={handleCreateCustomExercise}
                                  disabled={activeSessionLocked}
                                  fullWidth
                                >
                                  + CREATE CUSTOM EXERCISE
                                </Button>
                              </Box>
                            </Box>
                    }
                            </Box>
                          </Card>
                        </Grid>
                        {/* Exercise/Advice Library - 7/12 (hidden on sm, shown in drawer) */}
                        {!isSmDown && (
                          <Grid item xs={12} sm={12} md={6} lg={7} xl={8}>
                            <Box mt={0}>
                              <ExerciseLibrary
                                onAddExercise={handleAddExercise}
                                exercises={libraryExercises || []}
                                initialFilters={treatmentExerciseLibraryFilters}
                                initialFiltersKey={treatmentExerciseLibraryFiltersKey}
                                phoneUploadContext={{
                                  patientId: selectedPatient?.user_id || selectedPatient?.id || null,
                                  treatmentId: selectedPatient?.treatment_id || props.treatmentId || selectedInsuranceTreatmentId || null,
                                }}
                              />
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    ) : (
                      <Grid container spacing={1}>
                      <Grid item xs={12} sm={12} md={6} lg={5} xl={4}>
                        <Card style={{ background: '#f7faff', borderRadius: 16, boxShadow: '0 6px 32px rgba(0,45,85,0.18)', overflow: 'hidden' }}>
                          <div className={classes.panelHeader}>
                            <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', flexShrink: 0, paddingLeft: 5 }}>Advice</Typography>
                            <Box display="flex" alignItems="center" justifyContent="center" style={{ flex: 1, gap: 6 }}>
                              {!selectedPhaseId ? (
                                <Typography variant="subtitle1" style={{ color: "#FF7F27", fontStyle: "italic", fontSize: 'clamp(0.69rem, 1.04vw, 0.93rem)' }}>
                                  Select a patient to view program
                                </Typography>
                              ) : (
                                <Select disableUnderline displayEmpty
                                  value={selectedPhaseId}
                                  onChange={e => { handlePhaseDropdownChange(e); }}
                                  className={classes.orangeSelect}
                                  style={{ maxWidth: 230 }}
                                  MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8  } } }}
                                >
                                  {exercisePhases.map(option => (
                                    <MenuItem key={option.value} value={option.value} className={classes.menuItem}
                                      style={{ fontWeight: selectedPhaseId === option.value ? 700 : 500 }}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                              <Select disableUnderline
                                value={showAdviceInExerciseTab ? "advice" : "exercises"}
                                onChange={e => setShowAdviceInExerciseTab(e.target.value === "advice")}
                                className={classes.orangeSelect}
                                MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8  } } }}
                              >
                                <MenuItem value="exercises" className={classes.menuItem}>Exercises</MenuItem>
                                <MenuItem value="advice" className={classes.menuItem}>Advice</MenuItem>
                              </Select>
                            </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                              <IconButton
                                size="small"
                                onClick={handleSaveAllAdvices}
                                className={`${classes.iconButton} ${classes.panelIconButton}`}
                                style={{ marginLeft: 4 }}
                                title="Save all advices"
                              >
                                <SaveIcon />
                              </IconButton>
                              {typeof fetchPatientAllData === 'function' && (
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    if (selectedPatient && selectedPatient.user_id) {
                                      const diagId = Array.isArray(diagnosis) ? (diagnosis[0]?.diagnosis_id || diagnosis[0]?.id) : (diagnosis?.diagnosis_id || diagnosis?.id);
                                      const treatmentId = selectedPatient.treatment_id || null;
                                      try {
                                        await fetchPatientAllData(selectedPatient.user_id, diagId || null, treatmentId);
                                      } catch (err) {
                                        console.warn('Refresh (fetchPatientAllData) failed', err);
                                      }
                                    }
                                    if (selectedPatient && selectedPatient.user_id) {
                                      const status = await fetchActiveSessionStatus(selectedPatient.user_id, token);
                                      if (status && status.active) {
                                        setActiveSessionInfo(status);
                                        setActiveSessionLocked(true);
                                      } else {
                                        setActiveSessionInfo(null);
                                        setActiveSessionLocked(false);
                                      }
                                    }
                                  }}
                                  className={`${classes.iconButton} ${classes.panelIconButton}`}
                                  style={{ marginLeft: 4 }}
                                  title="Refresh advices"
                                >
                                  <RefreshIcon />
                                </IconButton>
                              )}
                            </Box>
                          </div>
                          <Box style={{ padding: 12 }}>
                        <Box p={0} position="relative">
                          {activeSessionLocked && (
                            <Box
                              position="absolute"
                              top={0}
                              left={0}
                              right={0}
                              bottom={0}
                              zIndex={10}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' }}
                            >
                              <Alert severity="warning" style={{ maxWidth: 520 }}>
                                Patient has an active session. Editing is temporarily locked. Please retry after the session ends. Click refresh button to re-check status.
                              </Alert>
                            </Box>
                          )}
                          <SelectedPatientAdvices
                            key={`advices-${selectedPatient?.user_id || 'none'}-${selectedPatient?.treatment_id || 'none'}-${selectedPhaseId}`}
                            phases={exercisePhases}
                            selectedPhaseId={selectedPhaseId}
                            selectedPatient={selectedPatient}
                            treatmentId={selectedPatient?.treatment_id}
                            advices={editableAdvices}
                            phaseName={currentPhaseNameForAdvices}
                            exercisesState={exercisesState}
                            setExercisesState={setExercisesState}
                            handleRemoveAdviceFrontend={handleRemoveAdviceFrontend}
                            handleSaveAllAdvices={handleSaveAllAdvices}
                            onAddAdvice={handleAddAdvice}
                            onUpdateAdviceImportance={handleUpdateAdviceImportance}
                            onEditAdvice={handleOpenEditAdviceDialog}
                          />
                        </Box>
                          </Box>
                        </Card>
                      </Grid>
                      {!isSmDown && (
                        <Grid item xs={12} sm={12} md={6} xl={8}>
                          <Box p={0}>
                            <AdviceLibrary
                              key="advice-library-singleton"
                              className={classes.orangeFocused}
                              onAddAdvice={handleAddAdvice}
                              handleRemoveAdviceFrontend={handleRemoveAdviceFrontend}
                            />
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                                          )}
                  </>
                )}
                {activeTab === 'advice' && (
                  <Box p={2}>
                    <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, fontSize: 22 }}>Advice & Education</Typography>
                    <Typography variant="body2" color="textSecondary">(Coming soon...)</Typography>
                  </Box>
                )}
                {activeTab === 'journal' && (
                  <SelectedPatientJourney
                    key={`journey-${selectedPatient?.treatment_id}-${activeDiagnosis?.id || 'none'}`}
                    selectedPatient={selectedPatient}
                    treatmentId={selectedPatient?.treatment_id}
                    diagnosis={activeDiagnosis}
                                   libraryExercises={libraryExercises || []}
                                   exercises={exercises || []}
                                   allPhaseAdvices={allPhaseAdvices || []}
                                   phases={phases || []}
                                   selectedPhaseId={selectedPhaseId}
                                   setSelectedPhaseId={setSelectedPhaseId}
                                   onSaveJourneyAdvices={saveAdvicesForPhase}
                                   fetchExercisesForPhase={fetchExercisesForPhase}
                  />
                )}
                {activeTab === 'Overview' && (
                  <SelectedPatientDiagnosis 
                    selectedPatient={selectedPatient}
                    treatmentId={selectedPatient?.treatment_id}
                    diagnosis={activeDiagnosis}
                    userToken={token}
                    assessmentDate={assessmentDate}
                    patientAdvices={advices}
                    patientPhases={phases}
                    setActiveTab={setActiveTab}
                    setShowAdviceInExerciseTab={setShowAdviceInExerciseTab}
                    onOpenReportDialog={handleOpenReportDialog}
                    onJournalSectionsUpdate={setCurrentJournalSections}
                    onDiagnosisUpdate={setCurrentAppliedDiagnosis}
                    onEditInProgramBuilder={(phaseId) => {
                      if (phaseId) {
                        try {
                          setSelectedPhaseId(phaseId);
                        } catch (e) {
                          // ignore if setter not provided by parent
                        }
                      }
                      setActiveTab('exerciseProgram');
                    }}
                  />
                )}
                {activeTab === 'patientDetails' && selectedPatient && (
                  <Box
                    key={`patient-details-${selectedPatient?.user_id}-${selectedPatient?.treatment_id || 'none'}`}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                    height="100%"
                    gap={2}
                  >
                    {patientDetailsTab === 'payment' && (
                      <Box display="flex" flexDirection="column" width="100%" gap={3}>
                        <SelectedPatientPaymentSubscription
                          selectedPatient={selectedPatient}
                          headerTitle="Payment overview"
                          headerTabs={(
                            <Button
                              variant="contained"
                              size="small"
                              style={{ backgroundColor: '#002D55', color: '#fff', textTransform: 'none', whiteSpace: 'nowrap', borderRadius: 18 }}
                              onClick={openClinicStatistics}
                            >
                              Open clinic statistics
                            </Button>
                          )}
                        />
                      </Box>
                    )}
                    {patientDetailsTab === 'personalInformation' && (
                      <Box display="flex" flexDirection="column" width="100%" gap={4}>
                        <SelectedPatientProfileDetails
                          selectedPatient={selectedPatient}
                          headerTitle="Profile"
                          onProfileSaved={onProfileSaved}
                        />
                      </Box>
                    )}
                    {patientDetailsTab === 'insurance' && (
                      <Box display="flex" flexDirection="column" width="100%" gap={3}>
                        <SelectedPatientInsurance
                          selectedPatient={selectedPatient}
                          treatments={deriveTreatmentsFromProfile(selectedPatient)}
                          appointments={selectedPatient?.appointments || []}
                          selectedTreatmentId={selectedInsuranceTreatmentId}
                          onTreatmentChange={setSelectedInsuranceTreatmentId}
                          headerTitle="Insurance Provider Information"
                        />
                      </Box>
                    )}
                  </Box>
                )}
                {activeTab === 'clinicCalendar' && (
                    <EHRBookingCalendar 
                      selectedPatientId={props.selectedPatientId}
                      selectedPatientName={props.selectedPatientName}
                      selectedTreatmentId={selectedTreatmentId}
                      profiles={props.allProfiles}
                    />
                )}
            </Grid>
          </Grid>
        </Container>
      {/* Snackbar for save feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
      <Dialog
        open={Boolean(programJournalConfirmation)}
        onClose={() => setProgramJournalConfirmation(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 18, maxWidth: 640 } }}
      >
        <DialogTitle className={classes.editAdviceDialogTitle}>
          <Box className={classes.editAdviceDialogTitleRow}>
            <Typography className={classes.editAdviceDialogTitleText}>Add program changes to journal?</Typography>
            <IconButton onClick={() => setProgramJournalConfirmation(null)} className={classes.editAdviceDialogCloseButton}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.editAdviceDialogContent}>
          <Typography className={classes.editAdviceDialogSectionLabel}>Saved program changes</Typography>
          <Box className={classes.editAdviceDialogSectionPanel} style={{ marginBottom: 0 }}>
            {(programJournalConfirmation?.changeSummary?.journalLines || []).map((line, index) => (
              <Typography key={`${line}-${index}`} style={{ color: '#002D55', fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)', lineHeight: 1.55, marginBottom: index === programJournalConfirmation.changeSummary.journalLines.length - 1 ? 0 : 8 }}>
                {line}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions className={classes.editAdviceDialogActions}>
          <Button onClick={handleReviewProgramJournal} variant="outlined" className={classes.cancelButton}>
            Review in journal
          </Button>
          <Button onClick={handleConfirmProgramJournal} variant="contained" className={classes.confirmButton}>
            Add to journal
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Exercise Dialog */}
      <ExerciseEditDialog
        open={editExerciseDialogOpen}
        onClose={handleCloseEditExerciseDialog}
        onConfirm={handleConfirmEditExercise}
        selectedExercise={selectedExerciseToEdit}
        editedParams={editedExerciseParams}
        onParamChange={handleEditExerciseParamChange}
        dialogTitle="Edit Exercise"
        libraryExercises={libraryExercises || []}
        phoneUploadContext={{
          patientId: selectedPatient?.user_id || selectedPatient?.id || null,
          treatmentId: selectedPatient?.treatment_id || props.treatmentId || selectedInsuranceTreatmentId || null,
          allowDraftUpload: Boolean(selectedExerciseToEdit?.isNewCustomExercise && !selectedExerciseToEdit?.id),
          autoStartOnOpen: Boolean(selectedExerciseToEdit?.isNewCustomExercise && !selectedExerciseToEdit?.id),
        }}
      />

      {/* Edit Advice Dialog */}
      <Dialog
        open={phaseProgressionDialogOpen}
        onClose={handleClosePhaseProgressionDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { borderRadius: 18, maxWidth: 860 } }}
      >
        <DialogTitle className={classes.editAdviceDialogTitle}>
          <Box className={classes.editAdviceDialogTitleRow}>
            <Typography className={classes.editAdviceDialogTitleText}>Program settings</Typography>
            <IconButton onClick={handleClosePhaseProgressionDialog} className={classes.editAdviceDialogCloseButton}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.editAdviceDialogContent}>
          <Accordion
            expanded={phaseProgressionSection === 'settings'}
            onChange={handlePhaseProgressionSectionChange('settings')}
            elevation={0}
            style={{ borderRadius: 16, border: '1px solid #d8e2ef', marginBottom: 16, overflow: 'hidden' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#002D55' }} />} style={{ background: '#f4f7fb' }}>
              <Box>
                <Typography className={classes.editAdviceDialogSectionLabel} style={{ marginBottom: 0 }}>Exercise adjustments</Typography>
                <Typography style={{ fontSize: 12, color: '#5f6f82' }}>
                  Control whether this treatment follows adaptive exercise replacement or stays manual.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails style={{ display: 'block', padding: 0 }}>
              <Box className={classes.editAdviceDialogSectionPanel} style={{ marginBottom: 0, borderRadius: 0, border: 'none', boxShadow: 'none' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography style={{ fontSize: 12, fontWeight: 700, color: '#002D55', marginBottom: 6 }}>Treatment adjustment mode</Typography>
                    <Select
                      fullWidth
                      disableUnderline
                      value={adjustmentMode}
                      onChange={(event) => handleAdjustmentModeChange(event.target.value)}
                      disabled={adjustmentModeLoading}
                      className={classes.orangeSelect}
                      style={{ width: '100%' }}
                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}
                    >
                      {EXERCISE_ADJUSTMENT_MODE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value} className={classes.menuItem}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={phaseProgressionSection === 'rules'}
            onChange={handlePhaseProgressionSectionChange('rules')}
            elevation={0}
            style={{ borderRadius: 16, border: '1px solid #d8e2ef', marginBottom: 16, overflow: 'hidden' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#002D55' }} />} style={{ background: '#f4f7fb' }}>
              <Box>
                <Typography className={classes.editAdviceDialogSectionLabel} style={{ marginBottom: 0 }}>Phase Progression Criteria</Typography>
                <Typography style={{ fontSize: 12, color: '#5f6f82' }}>Choose or edit progression criteria for the selected transition.</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails style={{ display: 'block', padding: 0 }}>
              <Box className={classes.editAdviceDialogSectionPanel} style={{ marginBottom: 0, borderRadius: 0, border: 'none', boxShadow: 'none' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Select
                      fullWidth
                      disableUnderline
                      value={selectedPhaseTransitionKey}
                      onChange={handlePhaseTransitionSelect}
                      className={classes.orangeSelect}
                      style={{ width: '100%' }}
                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}
                    >
                      {(phaseProgressionData?.transitions || []).map((transition) => (
                        <MenuItem key={getPhaseTransitionKey(transition)} value={getPhaseTransitionKey(transition)} className={classes.menuItem}>
                          {(transition.source_phase_name || `Phase ${transition.source_phase_index}`)} {'->'} {(transition.target_phase_name || `Phase ${transition.target_phase_index}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
                <Box style={{ marginTop: 16, marginBottom: 8, padding: '10px 12px', borderRadius: 14, background: '#f4f7fb' }}>
                  {selectedPhaseTransition ? (
                    <Box style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 12, border: '1px solid #d8e2ef', background: '#ffffff' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                        <Typography style={{ fontSize: 12, fontWeight: 700, color: '#002D55' }}>
                          Current source: {selectedPhaseTransitionSource.label}
                        </Typography>
                        {selectedPhaseTransitionSource.key === 'legacy_fallback' ? (
                          <Typography style={{ fontSize: 11, fontWeight: 700, color: '#b26a00', background: '#fff4d6', borderRadius: 999, padding: '4px 8px' }}>
                            Review before saving
                          </Typography>
                        ) : null}
                      </Box>
                      {selectedPhaseTransitionSource.summary ? (
                        <Typography style={{ fontSize: 12, color: '#5f6f82', lineHeight: 1.45 }}>
                          {selectedPhaseTransitionSource.summary}
                        </Typography>
                      ) : null}
                      {selectedPhaseTransitionSource.key !== 'legacy_fallback' ? (
                        selectedPhaseTransitionSource.notes.map((note, index) => (
                          <Typography key={`phase-source-note-${index}`} style={{ fontSize: 12, color: '#5f6f82', lineHeight: 1.45, marginTop: 6 }}>
                            {note}
                          </Typography>
                        ))
                      ) : null}
                      {selectedPhaseTransitionSource.key !== 'legacy_fallback' ? (
                        selectedPhaseTransitionSource.unrepresentedChecks.map((item, index) => (
                          <Typography key={`phase-source-check-${index}`} style={{ fontSize: 12, color: '#8a5200', lineHeight: 1.45, marginTop: 6 }}>
                            {item}
                          </Typography>
                        ))
                      ) : null}
                    </Box>
                  ) : null}
                  <Typography style={{ fontSize: 12, color: '#5f6f82', lineHeight: 1.45 }}>
                    The first required field in each rule combo must use either Sessions completed or Duration in phase (weeks). 
                  </Typography>
                  <Typography style={{ fontSize: 12, color: '#5f6f82', lineHeight: 1.45 }}>
                    Combine with other criterias tracked in Patient Reported Outcomes Measures (PROMs) for this patient as needed.
                  </Typography>
                </Box>
                <Box style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {phaseRuleDraftCombos.map((combo, comboIndex) => (
                    <Box key={combo.localId} style={{ border: '1px solid #d8e2ef', borderRadius: 16, padding: 12, background: '#fff' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography style={{ fontSize: 13, fontWeight: 700, color: '#002D55' }}>
                            Rule combo {comboIndex + 1}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: '#5f6f82' }}>
                            All criteria in this combo must pass.
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                          {comboIndex > 0 ? (
                            <TextField
                              select
                              label="Join"
                              value={combo.combo_join_operator}
                              onChange={(event) => handlePhaseComboJoinOperatorChange(combo.localId, event.target.value)}
                              SelectProps={{ MenuProps: PHASE_PROGRESSION_SELECT_MENU_PROPS }}
                              variant="outlined"
                              size="small"
                              className={`${classes.orangeFocused} ${classes.phaseProgressionCompactField} ${classes.phaseProgressionSelectField}`}
                              style={{ minWidth: 120 }}
                              InputLabelProps={{ shrink: true }}
                            >
                              {PHASE_PROGRESSION_COMBO_JOIN_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value} className={classes.menuItem}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleRemovePhaseConditionCombo(combo.localId)}
                            disabled={phaseRuleDraftCombos.length <= 1}
                            style={{ borderColor: '#ef9a9a', color: '#c62828', textTransform: 'none', borderRadius: 14, fontWeight: 700 }}
                          >
                            Remove combo
                          </Button>
                        </Box>
                      </Box>
                      <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {combo.conditions.map((condition, conditionIndex) => (
                          <Box key={`${combo.localId}-condition-${conditionIndex}`} style={{ border: '1px solid #d8e2ef', borderRadius: 16, padding: 12, background: '#fff' }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} md={8}>
                                <TextField
                                  select
                                  fullWidth
                                  label={conditionIndex === 0 ? 'Time' : 'Criteria'}
                                  value={condition.metric_key}
                                  onChange={(event) => handlePhaseConditionChange(combo.localId, conditionIndex, 'metric_key', event.target.value)}
                                  SelectProps={{ MenuProps: PHASE_PROGRESSION_SELECT_MENU_PROPS }}
                                  variant="outlined"
                                  size="small"
                                  className={`${classes.orangeFocused} ${classes.phaseProgressionCompactField} ${classes.phaseProgressionSelectField}`}
                                  InputLabelProps={{ shrink: true }}
                                >
                                  {getAvailablePhaseProgressionMetricOptions(combo.conditions, conditionIndex, phaseProgressionMetricOptions).map((option) => (
                                    <MenuItem key={option.key} value={option.key} className={classes.menuItem}>{option.label}</MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label={getPhaseProgressionThresholdLabel(condition)}
                                  type="number"
                                  value={condition.threshold_value}
                                  onChange={(event) => handlePhaseConditionChange(combo.localId, conditionIndex, 'threshold_value', event.target.value)}
                                  variant="outlined"
                                  size="small"
                                  className={`${classes.orangeFocused} ${classes.phaseProgressionCompactField}`}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={12} md={1}>
                                <Box display="flex" justifyContent="flex-end">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemovePhaseCondition(combo.localId, conditionIndex)}
                                    disabled={conditionIndex === 0 || combo.conditions.length <= 1}
                                    style={{ color: '#FF7F27' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Grid>
                              {condition.metric_key === 'exercise_level_progressions' && (
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Exercise identifiers (comma separated)"
                                    value={condition.exercise_identifiers_text}
                                    onChange={(event) => handlePhaseConditionChange(combo.localId, conditionIndex, 'exercise_identifiers_text', event.target.value)}
                                    variant="outlined"
                                    size="small"
                                    className={classes.orangeFocused}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Optional: limit this rule to specific exercises."
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        ))}
                      </Box>
                      <Box display="flex" justifyContent="flex-end" style={{ marginTop: 12 }}>
                        <Button
                          onClick={() => handleAddPhaseCondition(combo.localId)}
                          disabled={combo.conditions.length >= 3}
                          variant="outlined"
                          style={{ borderColor: '#FF7F27', color: '#002D55', textTransform: 'none', borderRadius: 14, fontWeight: 700 }}
                        >
                          Add criterion
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 12, gap: 12, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        color="primary"
                        checked={rememberPhaseRuleForAllPatients}
                        onChange={(event) => setRememberPhaseRuleForAllPatients(event.target.checked)}
                      />
                    )}
                    label="Remember for all my patients"
                  />
                  <Button
                    onClick={handleAddPhaseConditionCombo}
                    variant="outlined"
                    style={{ borderColor: '#FF7F27', color: '#002D55', textTransform: 'none', borderRadius: 14, fontWeight: 700 }}
                  >
                    Add combo
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={phaseProgressionSection === 'promotion'}
            onChange={handlePhaseProgressionSectionChange('promotion')}
            elevation={0}
            style={{ borderRadius: 16, border: '1px solid #d8e2ef', overflow: 'hidden' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#002D55' }} />} style={{ background: '#f4f7fb' }}>
              <Box>
                <Typography className={classes.editAdviceDialogSectionLabel} style={{ marginBottom: 0 }}>Manual phase change</Typography>
                <Typography style={{ fontSize: 12, color: '#5f6f82' }}>Move directly to another phase, including regression to a lower phase, and bypass automatic checks.</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails style={{ display: 'block', padding: 0 }}>
              <Box className={classes.editAdviceDialogSectionPanel} style={{ marginBottom: 0, borderRadius: 0, border: 'none', boxShadow: 'none' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Typography style={{ fontSize: 12, fontWeight: 700, color: '#002D55', marginBottom: 6 }}>Move to phase</Typography>
                    <Select
                      fullWidth
                      disableUnderline
                      value={phasePromotionTarget}
                      onChange={(event) => setPhasePromotionTarget(event.target.value)}
                      className={classes.orangeSelect}
                      style={{ width: '100%' }}
                      MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}
                    >
                      {manualPromotionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value} className={classes.menuItem}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      label="Rationale (optional)"
                      value={phasePromotionRationale}
                      onChange={(event) => setPhasePromotionRationale(event.target.value)}
                      variant="outlined"
                      size="small"
                      className={classes.orangeFocused}
                      InputLabelProps={{ shrink: true }}
                      helperText="This writes an audit entry for the manual phase change."
                    />
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions className={classes.editAdviceDialogActions}>
          {phaseProgressionSection === 'rules' && selectedPhaseTransition?.override_rule && (
            <Button onClick={handleClearPhaseProgressionOverride} variant="outlined" className={classes.cancelButton} disabled={phaseProgressionSaving}>
              Clear patient override
            </Button>
          )}
          <Button onClick={handleClosePhaseProgressionDialog} variant="outlined" className={classes.cancelButton}>
            Close
          </Button>
          {phaseProgressionSection === 'rules' ? (
            <Button onClick={handleSavePhaseProgressionRule} variant="contained" className={classes.confirmButton} disabled={phaseProgressionSaving || !selectedPhaseTransition}>
              {selectedPhaseTransitionSource.key === 'legacy_fallback' ? 'Create patient rule' : 'Save rule'}
            </Button>
          ) : phaseProgressionSection === 'promotion' ? (
            <Button onClick={handleManualPhasePromotion} variant="contained" className={classes.confirmButton} disabled={phasePromotionLoading || !phasePromotionTarget}>
              Update phase
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={editAdviceDialogOpen}
        onClose={handleCloseEditAdviceDialog}
        fullWidth
        PaperProps={{ style: { borderRadius: 18, maxWidth: 800 } }}
      >
        <DialogTitle className={classes.editAdviceDialogTitle}>
          <Box className={classes.editAdviceDialogTitleRow}>
            <Typography className={classes.editAdviceDialogTitleText}>Edit Advice</Typography>
            <IconButton onClick={handleCloseEditAdviceDialog} className={classes.editAdviceDialogCloseButton}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.editAdviceDialogContent}>
          {selectedAdviceToEdit && (
            <>
              <Typography className={classes.editAdviceDialogSectionLabel}>Advice details</Typography>
              <Box className={classes.editAdviceDialogSectionPanel}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <TextField
                      label="Title"
                      fullWidth
                      value={editedAdviceParams.title}
                      onChange={(e) => handleEditAdviceParamChange('title', e.target.value)}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                      InputProps={{ style: { color: '#002D55', fontWeight: 600 } }}
                      className={classes.editAdviceDialogField}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="Advice"
                      multiline
                      minRows={3}
                      maxRows={6}
                      fullWidth
                      value={editedAdviceParams.advice}
                      onChange={(e) => handleEditAdviceParamChange('advice', e.target.value)}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                      InputProps={{ style: { color: '#002D55', fontWeight: 400, minHeight: 80  } }}
                      className={classes.editAdviceDialogField}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Typography className={classes.editAdviceDialogSectionLabel}>Ordering</Typography>
              <Box className={classes.editAdviceDialogSectionPanel} style={{ marginBottom: 0 }}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <TextField
                      label="Index / Importance (Lower numbers appear first)"
                      type="number"
                      fullWidth
                      value={editedAdviceParams.importance}
                      onChange={(e) => handleEditAdviceParamChange('importance', parseInt(e.target.value, 10) || 1)}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true, style: { color: '#002D55' } }}
                      InputProps={{ style: { color: '#002D55', fontWeight: 600 }, inputProps: { min: 1 } }}
                      className={classes.editAdviceDialogField}
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.editAdviceDialogActions}>
          <Button onClick={handleCloseEditAdviceDialog} variant="outlined" className={classes.cancelButton}>
            Cancel
          </Button>
          <Button onClick={handleConfirmEditAdvice} variant="contained" className={classes.confirmButton}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <ReportDialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        onSave={handleSaveReport}
        initialData={selectedReport}
        mode={reportMode}
        metrics={selectedPatient && selectedPatient._metrics ? selectedPatient._metrics : null}
        reportStats={reportStats}
        selectedPatient={selectedPatient}
        userToken={token}
        journalSections={currentJournalSections}
        appliedDiagnosis={currentAppliedDiagnosis}
        onEpicrisisGenerated={(response) => {
          console.log('Epicrisis generated:', response);
        }}
      />
      {/* Floating library button - visible on sm screens only, when in exerciseProgram tab */}
      {isSmDown && activeTab === 'exerciseProgram' && (
        <Button
          onClick={() => setLibraryDrawerOpen(true)}
          onMouseEnter={() => setLibBtnHovered(true)}
          onMouseLeave={() => setLibBtnHovered(false)}
          style={{
            position: 'fixed',
            right: 0,
            bottom: '15%',
            backgroundColor: libBtnHovered ? '#FF7F27' : '#002D55',
            padding: '40px 14px',
            color: '#fff',
            zIndex: 1200,
            boxShadow: libBtnHovered ? '0 6px 24px rgba(255,127,39,0.45)' : '0 4px 16px rgba(0,45,85,0.35)',
            borderRadius: '0 24px 24px 0',
            minWidth: 0,
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'none',
            lineHeight: 1.2,
            transition: 'background-color 0.2s, box-shadow 0.2s',
          }}
        >
          {showAdviceInExerciseTab ? 'Advice Library' : 'Exercise Library'}
        </Button>
      )}
      {/* Library Drawer - visible on sm screens */}
      <Drawer
        anchor="right"
        open={libraryDrawerOpen}
        onClose={() => setLibraryDrawerOpen(false)}
        PaperProps={{ style: { width: '75vw', background: '#f0f4fa', overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
      >
        <Box style={{ padding: 18, flex: 1, overflowY: 'auto' }}>
          {showAdviceInExerciseTab ? (
            <AdviceLibrary
              key="advice-library-drawer"
              className={classes.orangeFocused}
              onAddAdvice={handleAddAdvice}
              handleRemoveAdviceFrontend={handleRemoveAdviceFrontend}
            />
          ) : (
            <ExerciseLibrary
              key="exercise-library-drawer"
              onAddExercise={(ex) => { handleAddExercise(ex); setLibraryDrawerOpen(false); }}
              exercises={libraryExercises || []}
              initialFilters={treatmentExerciseLibraryFilters}
              initialFiltersKey={treatmentExerciseLibraryFiltersKey}
              phoneUploadContext={{
                patientId: selectedPatient?.user_id || selectedPatient?.id || null,
                treatmentId: selectedPatient?.treatment_id || props.treatmentId || selectedInsuranceTreatmentId || null,
              }}
            />
          )}
        </Box>
      </Drawer>
      </main>
    </React.Fragment>
  );
}

export default React.memo(SelectedPatientProgram); 